// DEPLOY: /var/www/woody.wtf/frontend/src/pages/ScansWorkspace.js
// Desktop scans workspace: scan tabs + chart panes.
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import api from '../api';
import ChartPage from './Chart2';   // new chart engine (old Highcharts page: ./Chart, /chart-old)

// ── Constants ────────────────────────────────────────────────────────────────
const EMA_OPTIONS      = ['8','9','12','20','21','26','50','70','100','150','200'];
const PERF_PERIODS     = ['1w','1m','3m','6m','1y','2y','3y','5y','10y','20y'];
const PERF_PERIOD_YEARS= { '1w':1/52,'1m':1/12,'3m':0.25,'6m':0.5,'1y':1,'2y':2,'3y':3,'5y':5,'10y':10,'20y':20 };
const SCROLL_STEP      = 120;
const PANEL_MIN        = 320;
const PANEL_MAX        = 0.85;
const MOBILE_BP        = 768;
const POLL_INTERVAL_MS = 5000;
const FILTERABLE_COLS  = new Set(['type','sector','industry','industry_group','sub_industry','exchange']);

const BASIC_COL_DEFS = [
  { key:'type',           label:'Type'        },
  { key:'sector',         label:'Sector'      },
  { key:'industry',       label:'Industry'    },
  { key:'industry_group', label:'Industry Group' },
  { key:'sub_industry',   label:'Sub-Industry'  },
  { key:'idx_tracked',    label:'Index'       },
  { key:'provider_name',  label:'Provider'    },
  { key:'expense_ratio',  label:'MER %'       },
  { key:'market_cap',     label:'Market Cap'  },
  { key:'last_price',     label:'Price'       },
  { key:'pe_ratio',       label:'P/E'         },
  { key:'dividend_yield', label:'Div Yield'   },
  { key:'volume',         label:'Volume'      },
  { key:'description',    label:'Description' },
];

const SCAN_SUB_COLS = [
  { key:'scan_type',          label:'Scan Type'  },
  { key:'pattern_start_date', label:'Pat. Start' },
  { key:'pattern_end_date',   label:'Pat. End'   },
  { key:'trigger_date',       label:'Trigger'    },
  { key:'target_price',       label:'Target $'   },
  { key:'status',             label:'Status'     },
];

const SCAN_TYPE_LABELS = {
  resistance_body:      'Resistance (Body)',
  resistance_wick:      'Resistance (Wick)',
  trendline:            'Descending Trendline',
  ascending_trendline:  'Ascending Trendline',
  support:              'Support Level',
  falling_wedge:        'Falling Wedge',
  rising_wedge:         'Rising Wedge',
  ascending_triangle:   'Ascending Triangle',
  descending_triangle:  'Descending Triangle',
  double_top:           'Double Top',
  double_bottom:        'Double Bottom',
  vdu_tightening:       'Volume Dry-Up / VCP',
  cup_and_handle:       'Cup & Handle',
  darvas_box:           'Darvas Box',
  bull_flag:            'Bull Flag',
  bull_pennant:         'Bull Pennant',
  head_and_shoulders:   'Head & Shoulders',
  inv_head_and_shoulders: 'Inverse Head & Shoulders',
  bear_flag:            'Bear Flag',
  high_tight_flag:      'High Tight Flag',
  engulfing:            'Engulfing Candle',
};

// All 15 addable pattern types shown in the dropdown
const PATTERN_MENU = [
  { scan_type:'resistance',           label:'Resistance Level' },
  { scan_type:'trendline',            label:'Descending Trendline' },
  { scan_type:'ascending_trendline',  label:'Ascending Trendline' },
  { scan_type:'support',              label:'Support Level' },
  { scan_type:'falling_wedge',        label:'Falling Wedge' },
  { scan_type:'rising_wedge',         label:'Rising Wedge' },
  { scan_type:'ascending_triangle',   label:'Ascending Triangle' },
  { scan_type:'descending_triangle',  label:'Descending Triangle' },
  { scan_type:'double_top',           label:'Double Top' },
  { scan_type:'double_bottom',        label:'Double Bottom' },
  { scan_type:'vdu_tightening',       label:'Volume Dry-Up / VCP' },
  { scan_type:'cup_and_handle',       label:'Cup & Handle' },
  { scan_type:'darvas_box',           label:'Darvas Box' },
  { scan_type:'bull_flag',            label:'Bull Flag' },
  { scan_type:'bull_pennant',         label:'Bull Pennant' },
  { scan_type:'head_and_shoulders',   label:'Head & Shoulders' },
  { scan_type:'inv_head_and_shoulders', label:'Inverse Head & Shoulders' },
  { scan_type:'bear_flag',            label:'Bear Flag' },
  { scan_type:'high_tight_flag',      label:'High Tight Flag' },
  { scan_type:'engulfing',            label:'Engulfing Candle' },
];

// Help text per pattern — description + parameter explanations
const PATTERN_HELP = {
  resistance: `A horizontal resistance level is a price zone where a stock has repeatedly been rejected. The more times price tests and fails to break through, the stronger and more significant the level becomes. When price finally breaks above a strong resistance zone on volume, it can be a powerful bullish signal as all the sellers who held at that level have now been exhausted.

This is one of the most foundational concepts in technical analysis, documented extensively by Edwards & Magee (Technical Analysis of Stock Trends, 1948) and refined by Murphy (Technical Analysis of the Financial Markets, 1999).

Parameters:
• Touch Type — Body uses the open/close tops of candles (more conservative). Wick uses the full high of each bar (more sensitive). Both finds either.
• Lookback (bars) — how many candles to search through. 200 bars ≈ 10 months of daily data.
• Min Touches — how many times price must have touched/tested the level. Higher values (7+) find more reliable, institutional-grade levels.
• Recent (bars) — the level must have been tested within this many recent bars, ensuring it is still relevant and active.`,

  trendline: `A descending trendline connects a series of lower swing highs, forming a declining resistance line. This is the defining feature of a stock in a downtrend. Each successive rally is weaker than the last, as sellers step in at lower prices. A decisive close above the trendline — especially on volume — signals that the downtrend may be breaking and a trend reversal or powerful rally could follow.

Source: Murphy, Technical Analysis of the Financial Markets, Ch. 4. One of the most widely used chart patterns by both retail and institutional traders.

Parameters:
• Min Touches — how many swing highs must touch or approach the trendline. More touches = more reliable line.
• Touch Tolerance % — how close a swing high must be to the trendline to count. 1.5% allows a small margin for imperfect touches.
• Swing Order (bars) — bars on each side needed to define a swing high. Higher values find more significant, widely-spaced highs.
• Min Decline % — the trendline must have declined by at least this percentage overall, ensuring a meaningful downtrend rather than a flat wobble.
• Breakout Window (days) — a breakout above the trendline must have occurred within this many recent days to be included.
• Lookback (days) — how far back in history to search for the trendline.`,

  ascending_trendline: `An ascending trendline connects a series of higher swing lows, forming a rising support line — the defining feature of a stock in an uptrend. Each pullback finds support at a higher level, showing that buyers are becoming more aggressive. A break below the trendline signals that the uptrend may be failing.

This is the bullish mirror of the descending trendline. Source: Murphy, Ch. 4. Widely used as a trend confirmation and trailing support tool.

Parameters:
• Min Touches — how many swing lows must touch or approach the trendline.
• Touch Tolerance % — how close a swing low must be to count as a touch.
• Swing Order (bars) — bars on each side needed to define a swing low.
• Min Rise % — the trendline must have risen by at least this percentage overall, confirming a genuine uptrend.
• Breakout Window (days) — a break below the trendline (bearish signal) must be within this many recent days.
• Lookback (days) — how far back to search.`,

  support: `A horizontal support level is the bullish mirror of resistance — a price zone where buyers have repeatedly stepped in to prevent further declines. The more times price has bounced from a support level, the more significant and reliable it becomes. A break below strong support is a bearish signal; a successful test and hold of support is bullish.

Source: Edwards & Magee, Technical Analysis of Stock Trends. One of the most foundational TA concepts.

Parameters:
• Touch Type — Body uses candle body lows (open/close bottoms). Wick uses the full low of each bar.
• Lookback (bars) — how many candles to search through.
• Min Touches — how many times price must have bounced from the level.
• Recent (bars) — the level must have been tested within this many recent bars.`,

  falling_wedge: `A falling wedge forms when both the highs (resistance) and lows (support) of a stock are declining, but the lower trendline on the lows is falling more slowly than the upper line — causing the two lines to converge. This tightening of range signals decreasing selling pressure. The falling wedge is a bullish reversal or continuation pattern; a breakout above the upper trendline on volume can lead to a sharp rally.

Source: Bulkowski, Encyclopedia of Chart Patterns (2nd ed.) — historically one of the most reliable bullish breakout patterns with a success rate around 68%.

Parameters:
• Min Touches (each line) — how many swing highs/lows must touch each trendline.
• Touch Tolerance % — margin allowed for imperfect touches.
• Swing Order (bars) — bars on each side to define a swing point.
• Min Decline % — how much the wedge must have declined overall.
• Convergence Required — the two lines must be converging (upper falls faster than lower).
• Lookback (days) — how far back to search.`,

  rising_wedge: `A rising wedge forms when both highs and lows are rising, but the upper resistance line rises more slowly than the lower support line, causing them to converge upward. This signals that buying momentum is weakening — the stock is making higher highs but with decreasing force. The rising wedge is a bearish pattern; a break below the lower trendline often leads to a significant decline.

Source: Bulkowski, Encyclopedia of Chart Patterns. A reliable bearish reversal signal, especially after an extended uptrend.

Parameters:
• Min Touches (each line) — how many swing points must touch each line.
• Touch Tolerance % — margin for imperfect touches.
• Swing Order (bars) — bars on each side to define a swing point.
• Min Rise % — how much the wedge must have risen overall.
• Lookback (days) — how far back to search.`,

  ascending_triangle: `An ascending triangle has a flat horizontal resistance line at the top (where sellers consistently step in) and a rising support trendline at the bottom (where buyers step in at higher and higher levels). This pattern shows that bulls are becoming increasingly aggressive while bears are holding at a fixed price. A breakout above the flat resistance — especially on strong volume — is a powerful bullish signal as those patient buyers finally overwhelm the sellers.

Source: Murphy, Technical Analysis of the Financial Markets, Ch. 6. O'Neil, How to Make Money in Stocks (base patterns). One of the most reliable continuation patterns.

Parameters:
• Resistance Tolerance % — how close the highs must be to the resistance level to count as flat.
• Min Resistance Touches — how many times price must test the flat top.
• Trendline Touches — how many rising lows must form the ascending support line.
• Touch Tolerance % — margin for trendline touches.
• Lookback (days) — how far back to search.`,

  descending_triangle: `A descending triangle is the bearish mirror of the ascending triangle. It has a flat horizontal support level at the bottom (where buyers consistently step in) and a declining resistance trendline at the top (where sellers step in at lower and lower prices). Bears are becoming increasingly aggressive. A break below the flat support is a bearish breakdown signal.

Source: Murphy, Ch. 6. Bulkowski, Encyclopedia of Chart Patterns. High probability bearish continuation pattern.

Parameters:
• Support Tolerance % — how flat the bottom lows must be.
• Min Support Touches — how many times price must test the flat bottom.
• Trendline Touches — how many declining highs must form the upper trendline.
• Touch Tolerance % — margin for trendline touches.
• Lookback (days) — how far back to search.`,

  double_top: `A double top is a classic bearish reversal pattern consisting of two swing highs at approximately the same price level, separated by a trough (the neckline). After failing to break above the same resistance level twice, sellers gain the upper hand. A close below the neckline (the trough between the two peaks) confirms the pattern and signals a potential significant decline — measured target is often the distance from the peak to the neckline, projected downward.

Source: Bulkowski, Encyclopedia of Chart Patterns — historically ~83% bearish breakout rate. One of the most reliable bearish reversal patterns in technical analysis.

Parameters:
• Peak Tolerance % — how close the two highs must be in price to count as a double top.
• Min Peak Separation (bars) — minimum number of bars between the two peaks to ensure a genuine pattern.
• Max Peak Separation (bars) — maximum bars between peaks.
• Neckline Break — whether price must have closed below the neckline trough to confirm.
• Lookback (days) — how far back to search.`,

  double_bottom: `A double bottom is the bullish mirror of the double top — two swing lows at approximately the same price level, separated by a peak. After failing to break below the same support level twice, buyers gain control. A close above the neckline (the peak between the two troughs) confirms the pattern. The measured target is the distance from the trough to the neckline, projected upward.

Source: Bulkowski, Encyclopedia of Chart Patterns — historically ~88% bullish breakout rate, making it one of the most reliable bullish reversal patterns.

Parameters:
• Trough Tolerance % — how close the two lows must be in price.
• Min Trough Separation (bars) — minimum bars between the two troughs.
• Max Trough Separation (bars) — maximum bars between troughs.
• Neckline Break — whether price must have closed above the neckline peak to confirm.
• Lookback (days) — how far back to search.`,

  vdu_tightening: `The Volatility Contraction Pattern (VCP), popularised by Mark Minervini, identifies stocks that are "coiling" — the price range (high minus low) and volume are both contracting over consecutive bars. This tightening signals that supply is being absorbed and selling pressure is exhausting. Stocks in a VCP often break out explosively once the contraction ends. It is related to Darvas's box theory and Jesse Livermore's concept of pivotal points.

Source: Minervini, Trade Like a Stock Market Wizard, Ch. 8 (VCP). Also referenced in O'Neil's CANSLIM system as a base characteristic.

Parameters:
• Contraction Bars — how many consecutive bars must show narrowing range and volume.
• Max Range Expansion % — the range cannot expand by more than this percentage on any bar during contraction.
• Volume Trend — whether volume must also be declining during the contraction period.
• Lookback (days) — how far back to search for the pattern.`,

  cup_and_handle: `The cup and handle is one of the most powerful and reliable bullish continuation patterns, popularised by William O'Neil in his 1988 classic "How to Make Money in Stocks" and central to his CANSLIM investment system. After a strong uptrend, price consolidates in a smooth, rounded U-shape (the cup), then forms a smaller pullback or sideways drift (the handle) before breaking out to new highs. Volume typically dries up during the cup and handle formation, then surges on the breakout.

Research shows a 95% success rate in bull markets with an average profit of 54% (Liberated Stock Trader). Bulkowski documents similar reliability.

Parameters:
• Cup Depth % — how deep the cup can be as a percentage of the prior high. O'Neil recommended 12–33%; deeper cups are less reliable.
• Cup Length (bars) — minimum and maximum duration of the cup formation. O'Neil: 7–65 weeks on weekly charts.
• Handle Depth % — maximum pullback of the handle from the cup rim. Shallower is better; >50% is a warning sign.
• Handle Length (bars) — how long the handle consolidation lasts.
• Prior Uptrend % — the stock must have risen by at least this percentage before the cup forms.
• Lookback (days) — how far back to search.`,

  darvas_box: `Nicolas Darvas was a professional dancer who turned $10,000 into $2,000,000 in the 1950s using a method he documented in "How I Made $2,000,000 in the Stock Market" (1960). The Darvas Box identifies stocks that have made a new high and then consolidated within a tight price range (the box). A box is confirmed when price sets a new high and then fails to make a new high for three consecutive bars. A breakout above the box top on strong volume is the buy signal. Consecutive breakout boxes stacking higher indicate sustained momentum.

The method combines momentum (buying new highs), consolidation (the box), and volume confirmation. Darvas only traded in uptrending sectors with strong fundamentals. The strategy has been validated by numerous algorithmic backtests as a trend-following system.

Parameters:
• Box Confirmation Bars — how many bars price must fail to set a new high before the box top is confirmed (Darvas used 3).
• Min Box Width (bars) — minimum duration of the consolidation before a breakout is valid.
• Max Box Depth % — maximum price range of the box as a percentage. Tighter boxes are stronger.
• Volume Spike on Breakout — whether a volume surge must accompany the breakout.
• Lookback (days) — how far back to search.`,

  bull_flag: `A bull flag forms after a sharp, near-vertical price advance (the flagpole) followed by a brief consolidation that drifts slightly lower or sideways within two parallel, gently downward-sloping trendlines (the flag). Volume typically surges during the flagpole and dries up during the flag — this quiet consolidation is a sign of low selling pressure. A breakout above the flag's upper trendline on expanding volume signals resumption of the prior uptrend. The measured move target is the flagpole height added to the breakout point.

Source: Murphy, Technical Analysis of the Financial Markets, Ch. 6. Bulkowski, Encyclopedia of Chart Patterns. 63% win rate on daily charts.

Parameters:
• Pole Min Gain % — how much the stock must have risen during the flagpole. Stronger poles make stronger flags.
• Pole Max Bars — maximum duration of the flagpole. Flags from fast, sharp moves are stronger.
• Flag Max Depth % — maximum pullback during the flag consolidation. More than 50% is a warning sign.
• Flag Max Bars — maximum duration of the flag consolidation before it becomes too extended.
• Volume Dry-Up — whether volume must decline during the flag phase.
• Lookback (days) — how far back to search.`,

  bull_pennant: `A bull pennant is very similar to a bull flag — it also requires a strong flagpole — but the consolidation phase forms a small symmetrical triangle with converging trendlines (higher lows + lower highs) rather than parallel lines. The converging tightening signals even greater balance between buyers and sellers before the eventual breakout. A close above the upper trendline of the pennant on volume confirms the continuation.

Source: Murphy, Ch. 6. Bulkowski, Encyclopedia of Chart Patterns. Trendoscope algorithmic pattern research. Slightly more rare than a bull flag but equally reliable.

Parameters:
• Pole Min Gain % — minimum flagpole advance.
• Pole Max Bars — maximum flagpole duration.
• Pennant Max Bars — maximum length of the converging triangle consolidation.
• Convergence Required — the trendlines must be demonstrably converging (not parallel).
• Volume ry-Up — whether volume must decline during pennant formation.
• Lookback (days) — how far back to search.`,

  head_and_shoulders: `The head and shoulders is one of the most famous and well-researched bearish reversal patterns. It consists of three swing highs: a left shoulder, a higher head (the highest point), and a right shoulder at approximately the same level as the left. The two troughs between the peaks define the neckline. When price breaks below the neckline, the pattern is confirmed and a significant decline typically follows — the measured target is the distance from the head to the neckline, projected downward.

Source: Bulkowski, Encyclopedia of Chart Patterns — average decline of 23% after breakout, 93% success rate. Academically studied by Lo, Mamaysky & Wang (Journal of Finance, 2000): 5–7% risk-adjusted excess returns per year.

Parameters:
• Shoulder Symmetry % — how close in price the left and right shoulders must be. Symmetric patterns are more reliable.
• Head Premium % — minimum amount the head must exceed the shoulders.
• Neckline Slope — whether the neckline must be roughly flat or can be sloped.
• Neckline Break — whether a close below the neckline is required for confirmation.
• Lookback (days) — how far back to search.`,

  inv_head_and_shoulders: `The inverse head and shoulders is the bullish mirror of the classic head and shoulders — a highly reliable bullish reversal pattern found at market bottoms. Three swing lows form: a left shoulder, a lower head (the deepest point), and a right shoulder at approximately the same level as the left. A break above the neckline on volume confirms the reversal and the measured target is the head-to-neckline distance projected upward.

Source: Bulkowski, Encyclopedia of Chart Patterns. Lo, Mamaysky & Wang (2000). One of the most reliable bullish reversal signals in technical analysis.

Parameters:
• Shoulder Symmetry % — how close the left and right shoulder lows must be.
• Head Discount % — minimum depth of the head below the shoulders.
• Neckline Break — whether a close above the neckline is required.
• Lookback (days) — how far back to search.`,

  bear_flag: `A bear flag is the bearish mirror of the bull flag. After a sharp, near-vertical decline (the flagpole), price consolidates in a gentle upward drift within two parallel, slightly upward-sloping trendlines. Volume dries up during consolidation. A break below the lower trendline of the flag on expanding volume signals resumption of the downtrend. The measured target is the flagpole height subtracted from the breakout point.

Source: Murphy, Technical Analysis of the Financial Markets, Ch. 6. Bulkowski, Encyclopedia of Chart Patterns.

Parameters:
• Pole Min Decline % — minimum decline during the flagpole.
• Pole Max Bars — maximum flagpole duration.
• Flag Max Rally % — maximum upward drift of the flag consolidation.
• Flag Max Bars — maximum flag duration.
• Volume Dry-Up — whether volume must decline during the flag.
• Lookback (days) — how far back to search.`,

  high_tight_flag: `The high tight flag is one of the rarest and most powerful patterns identified by William O'Neil. It requires a massive flagpole advance of 100% or more in 8 weeks or less — a sign of extreme institutional accumulation — followed by a tight, orderly pullback of no more than 10–25% over 3–5 weeks. When it breaks out, the high tight flag frequently produces gains of 100%+ from the breakout point. O'Neil called it the most powerful of all chart patterns.

Source: O'Neil, How to Make Money in Stocks. Because the pattern requires such extreme conditions, it is rare — but the hit rate and magnitude of gains when it does occur are exceptional.

Parameters:
• Pole Min Gain % — minimum advance for the flagpole. O'Neil required 100%+ in ≤8 weeks.
• Pole Max Bars — maximum duration of the flagpole (O'Neil: ≤40 trading days).
• Flag Max Depth % — maximum pullback of the flag. O'Neil: 10–25%. Deeper = lower quality.
• Flag Max Bars — maximum flag duration. Shorter is stronger.
• Lookback (days) — how far back to search.`,

  engulfing: `An engulfing candle is a two-bar reversal pattern where the body of the second candle completely engulfs the body of the first. A bullish engulfing forms when a down candle (close below open) is followed by a larger up candle (close above open) whose body swallows the previous body — sellers were overwhelmed by buyers. A bearish engulfing is the mirror: an up candle followed by a larger down candle.

Source: Nison, Japanese Candlestick Charting Techniques (1991). One of the most widely watched candlestick reversal signals.

Parameters:
• Direction — Bullish (down candle engulfed by up candle), Bearish (up candle engulfed by down candle), or Either.
• Within Last (days) — how many recent sessions to search, including today. 0 = today only; 5 = any of the last 5 sessions. The most recent match is reported; any days after it are ignored.`,
};

// ── Bar condition help text ───────────────────────────────────────────────────
const BAR_HELP = {
  vol_spike: `Volume spike detects when today's trading volume is significantly higher than the recent average. A sudden surge in volume often signals institutional buying or selling, and frequently precedes large price moves.

Parameters:
• Volume % of average — how much bigger today's volume must be vs the average. 150% means today's volume is at least 1.5× the average.
• Average over N days — how many previous days to calculate the average from. 20 days is a common baseline.`,

  big_green: `Big green candle identifies a strong bullish day — the stock closed higher than it opened, with a large body relative to the total candle range.

Parameters:
• Body % of candle range — the body (open to close) must be at least this percentage of the full high-to-low range. 60% means the body takes up most of the candle.
• vs avg candle size over N days — the body must also be larger than the average candle body over N previous days, confirming this candle is unusually large.`,

  range_expansion: `Range expansion detects when today's price range (high minus low) is significantly wider than yesterday's. A sudden expansion in range often signals the start of a new move or a breakout from consolidation.

Parameter:
• Range % greater than previous day — how much wider today's range must be. 50% means today's range is at least 1.5× yesterday's range.`,

  gap: `Gap detection identifies when today's opening price jumps significantly from yesterday's closing price, leaving a gap on the chart with no trading in between.

Parameters:
• Direction — Gap Up (opens higher), Gap Down (opens lower), or Either.
• Minimum gap % — the minimum percentage move between yesterday's close and today's open. 2% means a gap of at least 2%.`,

  inside_outside_bar: `Inside and outside bar patterns compare today's high-low range to yesterday's:

• Inside bar — today's entire range fits within yesterday's range. Signals a pause or consolidation. A breakout from an inside bar can be powerful.
• Outside bar (engulfing bar) — today's range completely engulfs yesterday's. Signals increased volatility and potential reversal or trend continuation.`,

  n_day_breakout: `N-day breakout detects when a stock's closing price breaks above its highest close in the past N days (bullish) or below its lowest close (bearish).

Parameters:
• Direction — break above the N-day high, or break below the N-day low.
• N (days) — the lookback window. Common values: 20 days (one month), 52 days (one quarter), 252 days (one year).`,

  week52_proximity: `Filters for stocks that are close to their 52-week (one year) high. These stocks are in a strong long-term uptrend and near the top of their recent range.

Parameter:
• Within N% of 52-week high — the stock's current price must be within this percentage of its highest close in the past 252 trading days. 5% means within 5% of its 52-week high.`,

  consecutive_closes: `Detects stocks that have closed higher (or lower) for multiple consecutive days in a row, signalling sustained directional momentum.

Parameters:
• Direction — consecutive higher closes (bullish) or lower closes (bearish).
• N days — how many consecutive days required. 3 is a common setting; 5+ is a stronger signal.`,

  ema_slope: `EMA slope checks whether a moving average is trending upward or downward over a recent window of days. Confirms trend direction without requiring a crossover.

Parameters:
• EMA — which moving average to check (e.g. EMA 50 for medium-term trend).
• Direction — Rising (EMA today is higher than N days ago) or Falling.
• Over N days — the lookback window for the slope. 5 days checks recent slope; 20 days checks longer-term direction.`,
};

// ── Snapshot metric help text ─────────────────────────────────────────────────
const METRIC_HELP = {
  market_cap: `Market Capitalisation is the total value of a company's shares — share price multiplied by shares on issue.

Range mode: enter minimum and/or maximum values in millions (e.g. 500 = $500M).

Tiers mode: select the size categories you want. US and ASX use different thresholds.
• Mega: >$200B | Large: $10B–$200B | Mid: $2B–$10B | Small: $300M–$2B | Micro: $50M–$300M | Nano: <$50M`,

  price: `Filters by the stock's last closing price in its local currency (AUD for ASX, USD for US stocks).

Enter a minimum price, maximum price, or both. For example: Min $1.00 excludes penny stocks; Min $0.50, Max $5.00 targets a specific price range.`,

  rsi: `RSI (Relative Strength Index) measures momentum on a scale of 0–100.

• Above 70 — overbought (strong momentum, potential reversal)
• Below 30 — oversold (selling pressure, potential bounce)
• 40–60 — neutral territory

Both fields are optional and independent — set one, both, or neither.`,

  stoch: `Stochastic %K is a momentum indicator (0–100) comparing closing price to recent price range. More sensitive than RSI to short-term moves.

• Below 20 — oversold zone
• Above 80 — overbought zone

Use min/max to define the range you want to find.`,

  volume: `Minimum daily trading volume (number of shares traded). Filters out illiquid stocks.

Common thresholds: 50,000 (light), 100,000 (moderate), 500,000+ (well-traded).`,

  div_yield: `Dividend yield is the annual dividend as a percentage of current price. A yield of 4% means $4/year per $100 invested.

Enter min and/or max as a percentage. Min 3 finds stocks paying at least 3%. Max 8 excludes very high yielders (which can signal distress).`,

  bb_position: `Bollinger Bands are drawn 2 standard deviations above and below a 20-day moving average.

• Above upper band — price is unusually high relative to recent history. Can signal a breakout or overbought conditions.
• Below lower band — price is unusually low. Can signal a breakdown or oversold conditions.
• Inside bands — price is within the normal range.`,

  atr: `ATR % = (ATR14 / Price) × 100. Measures how much a stock moves per day on average over 14 days, as a percentage of price.

• 1% ATR — relatively quiet stock
• 3%+ ATR — highly volatile

Set a minimum to filter out dormant stocks.`,

  obv: `OBV (On-Balance Volume) tracks cumulative buying and selling pressure — adding volume on up days, subtracting on down days.

Rising OBV = buyers in control. Falling OBV = sellers in control.

This scan checks whether OBV is higher or lower than it was N days ago.`,

  ema_rules: `EMA crossover rules compare two moving averages to identify trend direction.

• EMA 50 above EMA 200 — "Golden Cross" — long-term bullish signal
• EMA 20 above EMA 50 — medium-term uptrend
• EMA 9 below EMA 21 — short-term downtrend

All rules must be true for a stock to pass.`,

  price_ema_rules: `Price vs EMA rules check whether the current stock price is above or below a moving average.

• Price above EMA 200 — stock is in a long-term uptrend
• Price below EMA 50 — stock is under medium-term resistance

All rules must be true for a stock to pass.`,
};

// ── InlineHelp component ──────────────────────────────────────────────────────
// ── InlineHelp component ──────────────────────────────────────────────────────
function InlineHelp({ text, activeHelp, setActiveHelp }) {
  if (!text) return null;
  const isOpen = activeHelp === text;
  return (
    <button
      onClick={e => { e.stopPropagation(); setActiveHelp(isOpen ? '' : text); }}
      style={{
        background:'none', border:'none', cursor:'pointer', fontSize:'13px',
        padding:'0 2px', lineHeight:1,
        color: isOpen ? 'var(--accent)' : 'var(--text-muted)',
        opacity: isOpen ? 1 : 0.6,
        fontWeight: isOpen ? 700 : 400,
      }}
      title={isOpen ? 'Hide help' : 'Show help'}
    >ⓘ</button>
  );
}

// FieldLabel with inline help — renders label row, help expands below
function FieldLabel({ children, helpText, activeHelp, setActiveHelp, style: extraStyle }) {
  const isOpen = helpText && activeHelp === helpText;
  return (
    <div style={{ marginBottom:'4px', ...extraStyle }}>
      <span style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', fontFamily:"'DM Mono',monospace" }}>
        {children}
        {helpText && (
          <button
            onClick={e => { e.stopPropagation(); setActiveHelp(isOpen ? '' : helpText); }}
            style={{
              background:'none', border:'none', cursor:'pointer', fontSize:'13px',
              padding:'0 2px', lineHeight:1,
              color: isOpen ? 'var(--accent)' : 'var(--text-muted)',
              opacity: isOpen ? 1 : 0.6,
              fontWeight: isOpen ? 700 : 400,
            }}
            title={isOpen ? 'Hide help' : 'Show help'}
          >ⓘ</button>
        )}
      </span>
    </div>
  );
}


// ── Pattern defaults ──────────────────────────────────────────────────────────
const defaultResistance       = () => ({ scan_type:'resistance',           touch_type:'body', candles:200, min_touches:7, recent_bars:10 });
const defaultTrendline        = () => ({ scan_type:'trendline',            min_touches:4, touch_pct:1.5, swing_order:5, min_decline_pct:15.0, breakout_days:10, lookback_days:365 });
const defaultAscTrendline     = () => ({ scan_type:'ascending_trendline',  min_touches:4, touch_pct:1.5, swing_order:5, min_rise_pct:10.0, breakout_days:10, lookback_days:365 });
const defaultSupport          = () => ({ scan_type:'support',              touch_type:'wick', candles:200, min_touches:5, recent_bars:10 });
const defaultFallingWedge     = () => ({ scan_type:'falling_wedge',        min_touches:3, touch_pct:1.5, swing_order:5, min_decline_pct:10.0, lookback_days:365 });
const defaultRisingWedge      = () => ({ scan_type:'rising_wedge',         min_touches:3, touch_pct:1.5, swing_order:5, min_rise_pct:10.0, lookback_days:365 });
const defaultAscTriangle      = () => ({ scan_type:'ascending_triangle',   resistance_tol_pct:1.5, min_resistance_touches:3, trendline_touches:3, touch_pct:1.5, lookback_days:365 });
const defaultDescTriangle     = () => ({ scan_type:'descending_triangle',  support_tol_pct:1.5, min_support_touches:3, trendline_touches:3, touch_pct:1.5, lookback_days:365 });
const defaultDoubleTop        = () => ({ scan_type:'double_top',           peak_tol_pct:3.0, min_sep_bars:10, max_sep_bars:120, require_neckline_break:true, lookback_days:365 });
const defaultDoubleBottom     = () => ({ scan_type:'double_bottom',        trough_tol_pct:3.0, min_sep_bars:10, max_sep_bars:120, require_neckline_break:true, lookback_days:365 });
const defaultVDU              = () => ({ scan_type:'vdu_tightening',       contraction_bars:5, max_range_expansion_pct:10, volume_declining:true, lookback_days:180 });
const defaultCupHandle        = () => ({ scan_type:'cup_and_handle',       rim_tolerance_pct:5, cup_depth_min_pct:12, cup_depth_max_pct:50, cup_min_bars:30, prior_uptrend:false, lookback_days:500 });
const defaultDarvas           = () => ({ scan_type:'darvas_box',           confirm_bars:3, min_box_bars:5, max_box_depth_pct:15, vol_spike_on_break:true, lookback_days:365 });
const defaultBullFlag         = () => ({ scan_type:'bull_flag',            pole_min_gain_pct:15, pole_max_bars:20, flag_max_depth_pct:50, flag_max_bars:25, volume_dryup:true, lookback_days:180 });
const defaultBullPennant      = () => ({ scan_type:'bull_pennant',         pole_min_gain_pct:15, pole_max_bars:20, pennant_max_bars:20, volume_dryup:true, lookback_days:180 });
const defaultHeadShoulders    = () => ({ scan_type:'head_and_shoulders',   shoulder_sym_pct:10, head_premium_pct:3, require_neckline_break:true, lookback_days:365 });
const defaultInvHeadShoulders = () => ({ scan_type:'inv_head_and_shoulders', shoulder_sym_pct:10, head_discount_pct:3, require_neckline_break:true, lookback_days:365 });
const defaultBearFlag         = () => ({ scan_type:'bear_flag',            pole_min_decline_pct:15, pole_max_bars:20, flag_max_rally_pct:50, flag_max_bars:25, volume_dryup:true, lookback_days:180 });
const defaultHighTightFlag    = () => ({ scan_type:'high_tight_flag',      pole_min_gain_pct:100, pole_max_bars:40, flag_max_depth_pct:25, flag_max_bars:25, lookback_days:365 });
const defaultEngulfing        = () => ({ scan_type:'engulfing',            direction:'bullish', days_ago:0 });

const PATTERN_DEFAULTS = {
  resistance:             defaultResistance,
  trendline:              defaultTrendline,
  ascending_trendline:    defaultAscTrendline,
  support:                defaultSupport,
  falling_wedge:          defaultFallingWedge,
  rising_wedge:           defaultRisingWedge,
  ascending_triangle:     defaultAscTriangle,
  descending_triangle:    defaultDescTriangle,
  double_top:             defaultDoubleTop,
  double_bottom:          defaultDoubleBottom,
  vdu_tightening:         defaultVDU,
  cup_and_handle:         defaultCupHandle,
  darvas_box:             defaultDarvas,
  bull_flag:              defaultBullFlag,
  bull_pennant:           defaultBullPennant,
  head_and_shoulders:     defaultHeadShoulders,
  inv_head_and_shoulders: defaultInvHeadShoulders,
  bear_flag:              defaultBearFlag,
  high_tight_flag:        defaultHighTightFlag,
  engulfing:              defaultEngulfing,
};

const defaultBarConditions = () => ({
  vol_spike:          { enabled:false, pct:150, days:20 },
  big_green:          { enabled:false, body_pct:60, avg_days:10 },
  range_expansion:    { enabled:false, pct:50 },
  gap:                { enabled:false, direction:'up', pct:2 },
  inside_outside_bar: { enabled:false, type:'outside' },
  n_day_breakout:     { enabled:false, direction:'high', days:52 },
  week52_proximity:   { enabled:false, pct_from:5 },
  consecutive_closes: { enabled:false, direction:'up', count:3 },
  ema_slope:          { enabled:false, ema:'50', direction:'rising', days:5 },
});

const MCAP_TIERS_US = [
  { key:'mega',  label:'Mega',  min:200e9, max:Infinity },
  { key:'large', label:'Large', min:10e9,  max:200e9   },
  { key:'mid',   label:'Mid',   min:2e9,   max:10e9    },
  { key:'small', label:'Small', min:300e6, max:2e9     },
  { key:'micro', label:'Micro', min:50e6,  max:300e6   },
  { key:'nano',  label:'Nano',  min:0,     max:50e6    },
];
const MCAP_TIERS_ASX = [
  { key:'large', label:'Large', min:10e9,  max:Infinity },
  { key:'mid',   label:'Mid',   min:1e9,   max:10e9    },
  { key:'small', label:'Small', min:300e6, max:1e9     },
  { key:'micro', label:'Micro', min:50e6,  max:300e6   },
  { key:'nano',  label:'Nano',  min:0,     max:50e6    },
];

function mcapTierLabel(t) {
  const f = v => v>=1e12?(v/1e12).toFixed(0)+'T':v>=1e9?(v/1e9).toFixed(0)+'B':v>=1e6?(v/1e6).toFixed(0)+'M':'0';
  return t.max===Infinity?'>'+f(t.min):f(t.min)+'–'+f(t.max);
}

function defaultTabState(id) {
  return {
    _id: id,
    basicCols:    ['type','sector','market_cap','last_price'],
    perfCols:     ['1m','6m','1y'],
    perfType:     'drip',
    searchRules:  [{ id:0, connector:'AND', field:'all', op:'contains', value:'', caseSensitive:false }],
    filters: {
      mer_min:'',mer_max:'',mcap_min:'',mcap_max:'',
      price_min:'',price_max:'',rsi_min:'',rsi_max:'',
      stoch_min:'',stoch_max:'',volume_min:'',
      yield_min:'',yield_max:'',bb_position:'',
      pat_start_from:'',pat_start_to:'',
      pat_end_from:'',pat_end_to:'',
      trigger_from:'',trigger_to:'',
      target_min:'',target_max:'',
    },
    mcapMode:     'range',
    mcapTierDefs: 'us',
    mcapExcluded: new Set(),
    colExcluded:  {},
    emaRules:     [],
    priceEmaRules:[],
    sort:         { col:'symbol', dir:'ASC' },
    page:         1,
    rowsPerPage:  50,
    advOpen:      false,
  };
}

// ── Styles ───────────────────────────────────────────────────────────────────
const S = {
  root:          { height:'100vh', overflow:'hidden', background:'var(--bg)', display:'flex', flexDirection:'column' },
  nav:           { background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'0 20px', height:'52px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, zIndex:20 },
  logo:          { fontFamily:"'DM Mono',monospace", fontSize:'12px', fontWeight:500, letterSpacing:'0.1em', color:'var(--accent)', textTransform:'uppercase' },
  navRight:      { display:'flex', alignItems:'center', gap:'12px', fontSize:'13px', color:'var(--text-muted)' },
  navLink:       { color:'var(--text-muted)', fontSize:'12px', textecoration:'none', fontFamily:"'DM Mono',monospace" },
  navLinkAccent: { color:'var(--accent)', fontSize:'13px', textDecoration:'none' },
  logoutBtn:     { background:'none', border:'none', cursor:'pointer', color:'var(--accent)', fontSize:'13px', padding:'4px 10px' },
  body:          { flex:1, display:'flex', overflow:'hidden', position:'relative' },
  resizeHandle:  { width:'5px', flexShrink:0, background:'var(--border)', cursor:'col-resize', zIndex:10 },
  chartPanel:    (w) => ({ width:`${w}px`, flexShrink:0, display:'flex', flexDirection:'column', borderLeft:'1px solid var(--border)', background:'var(--bg)', overflow:'hidden' }),
  loadingScreen: { display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', color:'var(--text-muted)', fontSize:'13px' },
  scanPage:      { height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' },
  tabStripOuter: { background:'var(--surface)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', height:'38px', flexShrink:0, overflow:'hidden' },
  tabStripInner: { display:'flex', alignItems:'center', gap:'2px', overflowX:'auto', overflowY:'hidden', flex:1, height:'100%', padding:'0 4px', scrollbarWidth:'none', msOverflowStyle:'none' },
  scrollArrow:   { flexShrink:0, background:'var(--surface)', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'14px', height:'100%', padding:'0 6px', display:'flex', alignItems:'center' },
  tab: (a) => ({
    display:'flex', alignItems:'center', gap:'4px', padding:'4px 8px', borderRadius:'4px 4px 0 0',
    cursor:'pointer', fontSize:'11px', fontFamily:"'DM Mono',monospace", fontWeight:a?700:500,
    background:a?'var(--bg)':'transparent', color:a?'var(--accent)':'var(--text-muted)',
    border:a?'1px solid var(--border)':'1px solid transparent',
    borderBottom:a?'1px solid var(--bg)':'1px solid transparent',
    whiteSpace:'nowrap', userSelect:'none', flexShrink:0,
  }),
  tabBtn:        { background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'13px', padding:'1px 3px', borderRadius:'3px' },
  selectorBar:   { background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'5px 12px', display:'flex', alignItems:'center', gap:'8px', flexShrink:0 },
  selectorBtn:   { flexShrink:0, background:'none', border:'1px solid var(--border)', borderRadius:'4px', cursor:'pointer', color:'var(--text-muted)', fontSize:'11px', fontFamily:"'DM Mono',monospace", padding:'3px 8px', whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:'4px' },
  dropdown:      { position:'absolute', minWidth:'300px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 16px rgba(0,0,0,0.2)', zIndex:500, padding:'8px 0' },
  dropItem:      { display:'flex', alignItems:'center', gap:'8px', padding:'5px 12px', cursor:'pointer', fontSize:'12px', fontFamily:"'DM Mono',monospace" },
  runBarYellow:  { background:'#fef9c3', borderBottom:'1px solid #fde047', padding:'5px 12px', display:'flex', alignItems:'center', gap:'12px', fontSize:'12px', color:'#854d0e', flexShrink:0 },
  runBarBlue:    { background:'#eff6ff', borderBottom:'1px solid #bfdbfe', padding:'5px 12px', display:'flex', alignItems:'center', gap:'12px', fontSize:'12px', color:'#1d4ed8', flexShrink:0 },
  runBarRed:     { background:'#fef2f2', borderBottom:'1px solid #fecaca', padding:'5px 12px', display:'flex', alignItems:'center', gap:'12px', fontSize:'12px', color:'#dc2626', flexShrink:0 },
  runBarGreen:   { background:'#f0fdf4', borderBottom:'1px solid #bbf7d0', padding:'5px 12px', display:'flex', alignItems:'center', gap:'12px', fontSize:'12px', color:'#15803d', flexShrink:0 },
  runBtn:        { background:'#ca8a04', color:'#fff', border:'none', borderRadius:'var(--radius)', padding:'4px 14px', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:"'DM Mono',monospace" },
  advHeader:     { background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', fontWeight:600, fontSize:'13px', color:'var(--text)', margin:'8px 12px 0', flexShrink:0 },
  advContent:    { background:'var(--surface)', border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 var(--radius) var(--radius)', padding:'16px', display:'flex', flexDirection:'column', gap:'16px', margin:'0 12px', flexShrink:0, overflowY:'auto', maxHeight:'60vh' },
  colToggle:     (a) => ({ padding:'4px 10px', fontSize:'11px', fontWeight:600, border:`2px solid ${a?'var(--accent)':'var(--border)'}`, borderRadius:'20px', cursor:'pointer', background:a?'var(--accent)':'var(--surface)', color:a?'#fff':'var(--text-muted)', fontFamily:"'DM Mono',monospace", userSelect:'none' }),
  colGrid:       { display:'flex', flexWrap:'wrap', gap:'6px' },
  filterInput:   { padding:'5px 8px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'DM Mono',monospace", width:'80px' },
  filterSelect:  { padding:'5px 8px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'DM Mono',monospace" },
  filterGrid:    { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'12px' },
  filterLabel:   { fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", display:'block', marginBottom:'4px' },
  rangeRow:      { display:'flex', gap:'5px', alignItems:'center' },
  ruleRow:       { display:'flex', gap:'6px', alignItems:'center', marginBottom:'6px', flexWrap:'wrap' },
  ruleSelect:    { padding:'4px 6px', fontSize:'11px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'DM Mono',monospace" },
  removeBtn:     { padding:'2px 7px', border:'none', background:'#dc2626', color:'white', borderRadius:'3px', cursor:'pointer', fontSize:'11px' },
  addRuleBtn:    { padding:'3px 10px', fontSize:'11px', border:'2px solid var(--accent)', borderRadius:'20px', background:'var(--accent)', color:'#fff', cursor:'pointer', fontFamily:"'DM Mono',monospace", fontWeight:600 },
  btn:           { background:'var(--accent)', color:'#fff', border:'none', borderRadius:'var(--radius)', padding:'7px 18px', fontSize:'13px', fontWeight:600, cursor:'pointer' },
  btnSm:         { background:'var(--accent)', color:'#fff', border:'none', borderRadius:'var(--radius)', padding:'4px 12px', fontSize:'12px', fontWeight:600, cursor:'pointer' },
  btnSecondary:  { background:'var(--bg)', color:'var(--text-muted)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'7px 18px', fontSize:'13px', cursor:'pointer' },
  btnGhost:      { background:'none', color:'var(--text-muted)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'4px 10px', fontSize:'12px', cursor:'pointer' },
  btnDanger:     { background:'#dc2626', color:'#fff', border:'none', borderRadius:'var(--radius)', padding:'7px 18px', fontSize:'13px', fontWeight:600, cursor:'pointer' },
  tableArea:     { flex:1, overflow:'auto', padding:'8px 12px 12px', display:'flex', flexDirection:'column', gap:'8px', minHeight:0 },
  statsBar:      { display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'13px', color:'var(--text-muted)', flexShrink:0 },
  tableWrap:     { flex:1, overflow:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--surface)', minHeight:0 },
  table:         { width:'100%', borderCollapse:'collapse', fontSize:'12px', fontFamily:"'DM Mono',monospace" },
  th:            { padding:'8px 10px', textAlign:'left', borderBottom:'2px solid var(--border)', background:'var(--accent)', color:'#fff', position:'sticky', top:0, zIndex:10, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap', fontSize:'11px', fontWeight:700, textTransform:'uppercase' },
  thCheck:       { padding:'8px 10px', textAlign:'center', borderBottom:'2px solid var(--border)', background:'var(--accent)', color:'#fff', position:'sticky', top:0, zIndex:10, width:'36px' },
  thExpand:      { padding:'8px 6px', textAlign:'center', borderBottom:'2px solid var(--border)', background:'var(--accent)', color:'#fff', position:'sticky', top:0, zIndex:10, width:'28px' },
  td:            { padding:'7px 10px', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' },
  tdCheck:       { padding:'7px 10px', borderBottom:'1px solid var(--border)', textAlign:'center', width:'36px' },
  tdExpand:      { padding:'7px 6px', borderBottom:'1px solid var(--border)', textAlign:'center', width:'28px', cursor:'pointer', color:'var(--accent)', fontWeight:700, fontSize:'12px' },
  subTable:      { width:'100%', borderCollapse:'collapse', fontSize:'11px', fontFamily:"'DM Mono',monospace" },
  subTh:         { padding:'5px 10px', textAlign:'center', borderBottom:'1px solid var(--border)', background:'#e8eeff', color:'var(--accent)', cursor:'pointer', userSelect:'none', whiteSpace:'nowrap', fontSize:'10px', fontWeight:700, textTransform:'uppercase' },
  subTd:         { padding:'5px 10px', textAlign:'center', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap', color:'var(--text)' },
  positive:      { color:'#16a34a', fontWeight:600 },
  negative:      { color:'#dc2626', fontWeight:600 },
  badge:         { fontSize:'10px', padding:'2px 6px', borderRadius:'3px', fontWeight:700 },
  pagination:    { display:'flex', alignItems:'center', gap:'6px', justifyContent:'space-between', padding:'4px 0', flexShrink:0, flexWrap:'wrap' },
  pageBtn:       (a) => ({ padding:'3px 9px', fontSize:'12px', border:'1px solid var(--border)', borderRadius:'4px', cursor:'pointer', background:a?'var(--accent)':'var(--surface)', color:a?'#fff':'var(--text)', fontFamily:"'DM Mono',monospace" }),
  selBar:        { display:'flex', alignItems:'center', gap:'12px', background:'var(--accent-dim)', border:'1px solid var(--accent)', borderRadius:'var(--radius)', padding:'8px 12px', fontSize:'13px', flexShrink:0 },
  selCount:      { fontWeight:600, color:'var(--accent)', fontFamily:"'DM Mono',monospace" },
  missingNote:   { background:'#fef9c3', border:'1px solid #fde047', borderRadius:'4px', padding:'6px 12px', fontSize:'12px', color:'#854d0e', flexShrink:0 },
  colDropdown:   { position:'absolute', top:'100%', left:0, minWidth:'220px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 16px rgba(0,0,0,0.15)', zIndex:200, padding:'8px 0' },
  colDropItem:   { display:'flex', alignItems:'center', gap:'8px', padding:'5px 12px', cursor:'pointer', fontSize:'12px', fontFamily:"'DM Mono',monospace" },
  emptyState:    { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'12px', color:'var(--text-muted)', fontSize:'13px', fontFamily:"'DM Mono',monospace" },
  chartArea:     { flex:1, overflow:'hidden', display:'flex', flexDirection:'column' },
  noTabs:        { flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize:'12px', fontFamily:"'DM Mono',monospace", flexDirection:'column', gap:'8px' },
  modalOverlay:  { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' },
  modal:         { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'calc(var(--radius)*2)', padding:'28px', width:'860px', maxWidth:'95vw', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.25)' },
  modalSm:       { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'calc(var(--radius)*2)', padding:'28px', width:'480px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.25)' },
  modalTitle:    { fontSize:'16px', fontWeight:700, marginBottom:'4px', color:'var(--text)' },
  modalSub:      { fontSize:'12px', color:'var(--text-muted)', marginBottom:'20px' },
  modalLabel:    { fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", display:'block', marginBottom:'4px' },
  modalInput:    { width:'100%', padding:'7px 10px', fontSize:'13px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--bg)', color:'var(--text)', fontFamily:"'DM Sans',sans-serif", boxSizing:'border-box' },
  modalSection:  { marginBottom:'18px' },
  modalFooter:   { display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'24px' },
  modalError:    { color:'#dc2626', fontSize:'12px', marginTop:'8px' },
  patternBlock:  { border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px', marginBottom:'10px', background:'var(--bg)', position:'relative' },
  patternTitle:  { fontSize:'12px', fontWeight:700, color:'var(--accent)', fontFamily:"'DM Mono',monospace" },
  detailRow:     { display:'flex', gap:'8px', marginBottom:'8px', fontSize:'13px' },
  detailKey:     { color:'var(--text-muted)', fontFamily:"'DM Mono',monospace", fontSize:'11px', minWidth:'140px', fontWeight:700, textTransform:'uppercase', paddingTop:'2px' },
  detailVal:     { color:'var(--text)', flex:1 },
  detailChip:    { display:'inline-block', padding:'2px 8px', borderRadius:'12px', background:'var(--accent)', color:'#fff', fontSize:'11px', fontWeight:600, marginRight:'4px', marginBottom:'4px', fontFamily:"'DM Mono',monospace" },
};

const KEYFRAMES = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .modal-inner-tab { padding: 4px 12px; font-size: 11px; font-family: 'DM Mono', monospace; font-weight: 600; border-radius: 6px 6px 0 0; cursor: pointer; border: 1px solid transparent; border-bottom: none; display: flex; align-items: center; gap: 6px; user-select: none; transition: background 0.15s; }
  .modal-inner-tab.active { background: var(--bg); color: var(--accent); border-color: var(--border); }
  .modal-inner-tab:not(.active) { background: transparent; color: var(--text-muted); }
  .modal-inner-tab:not(.active):hover { background: var(--accent-dim, rgba(99,102,241,0.08)); }
  .modal-tab-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; font-size: 10px; font-weight: 700; background: var(--accent); color: #fff; }
  .modal-tab-badge.zero { background: var(--border); color: var(--text-muted); }
  .cond-toggle-row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid var(--border); }
  .cond-toggle-row:last-child { border-bottom: none; }
  .cond-toggle-label { font-size: 12px; font-weight: 600; color: var(--text); flex: 1; font-family: 'DM Mono', monospace; padding-top: 1px; }
  .cond-params { padding: 10px 0 4px 28px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
  .toggle-switch { position: relative; display: inline-block; width: 36px; height: 20px; flex-shrink: 0; margin-top: 1px; }
  .toggle-switch input { opacity: 0; width: 0; height: 0; }
  .toggle-slider { position: absolute; cursor: pointer; inset: 0; background: var(--border); border-radius: 20px; transition: 0.2s; }
  .toggle-slider:before { position: absolute; content: ''; height: 14px; width: 14px; left: 3px; bottom: 3px; background: white; border-radius: 50%; transition: 0.2s; }
  .toggle-switch input:checked + .toggle-slider { background: var(--accent); }
  .toggle-switch input:checked + .toggle-slider:before { transform: translateX(16px); }
  .summary-strip { display: flex; align-items: center; gap: 6px; padding: 8px 0 4px; overflow-x: auto; scrollbar-width: none; min-height: 34px; flx-wrap: nowrap; }
  .summary-strip::-webkit-scrollbar { display: none; }
  .summary-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 20px; background: var(--accent-dim, rgba(99,102,241,0.12)); border: 1px solid var(--accent); color: var(--accent); font-size: 11px; font-weight: 600; white-space: nowrap; font-family: 'DM Mono', monospace; flex-shrink: 0; }
  .summary-empty { font-size: 11px; color: var(--text-muted); font-family: 'DM Mono', monospace; font-style: italic; }
  .pattern-menu-dropdown { position: absolute; top: 100%; right: 0; z-index: 600; min-width: 240px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: 0 4px 16px rgba(0,0,0,0.18); padding: 6px 0; margin-top: 4px; max-height: 380px; overflow-y: auto; }
  .pattern-menu-item { padding: 7px 14px; font-size: 12px; font-family: 'DM Mono', monospace; cursor: pointer; color: var(--text); }
  .pattern-menu-item:hover { background: var(--accent-dim, rgba(99,102,241,0.1)); color: var(--accent); }
`;

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmt(v,dp=2)    { return v==null?'-':Number(v).toFixed(dp); }
function fmtPrice(v)    { if(v==null)return '-'; const p=Number(v); if(p>=10)return '$'+p.toFixed(2); if(p>=1)return '$'+p.toFixed(3); if(p>=0.01)return '$'+p.toFixed(4); return '$'+p.toFixed(6); }
function fmtMktCap(v)   { if(v==null)return '-'; if(v>=1e12)return '$'+(v/1e12).toFixed(2)+'T'; if(v>=1e9)return '$'+(v/1e9).toFixed(2)+'B'; if(v>=1e6)return '$'+(v/1e6).toFixed(0)+'M'; return '$'+v.toFixed(0); }
function fmtPct(v,dp=2) { return v==null?'-':(v*100).toFixed(dp)+'%'; }
function fmtMer(v)      { return v==null?'-':(v*100).toFixed(2)+'%'; }
function fmtVol(v)      { return v==null?'-':Number(v).toLocaleString(); }
function emaKey(n)      { return 'ema'+String(n).padStart(2,'0'); }
function annualize(cumPct, period) {
  const y=PERF_PERIOD_YEARS[period];
  if(cumPct==null)return null;
  if(y<=1)return cumPct;
  const dec=cumPct/100;
  if(dec<=-0.99||dec>=1000)return null;
  const val=(Math.pow(1+dec,1/y)-1)*100;
  return isFinite(val)&&Math.abs(val)<100000?val:null;
}

function groupByTicker(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = `${row.symbol}:${row.exchange}`;
    if (!map.has(key)) map.set(key, { ...row, _scanRows:[row] });
    else map.get(key)._scanRows.push(row);
  }
  return [...map.values()];
}

function sortScanRows(rows, col, dir) {
  return [...rows].sort((a,b)=>{
    let av=a[col], bv=b[col];
    if(av==null&&bv==null)return 0; if(av==null)return 1; if(bv==null)return -1;
    const na=parseFloat(av),nb=parseFloat(bv);
    if(!isNaN(na)&&!isNaN(nb))return dir==='ASC'?na-nb:nb-na;
    return dir==='ASC'?String(av).localeCompare(String(bv)):String(bv).localeCompare(String(av));
  });
}

function getStaleness(tabDef, ingestionStatus) {
  if(!tabDef) return null;
  if(tabDef.last_run_status==='error') return { type:'error', message:`Scan error: ${tabDef.last_run_error||'Unknown error'}` };
  if(tabDef.last_run_status==='running'||tabDef.last_run_status==='pending') return { type:'running', message: tabDef.last_run_status==='pending'?'Scan queued…':'Scan running…' };
  const rules = tabDef.rules_json||{};
  const exchanges = rules.exchanges?.length ? rules.exchanges : ['AU','US'];
  const needsAU = exchanges.includes('AU');
  const needsUS = exchanges.includes('US');
  const auStatus  = ingestionStatus?.AU?.ingestion_status;
  const usStatus  = ingestionStatus?.US?.ingestion_status;
  const auLatest  = ingestionStatus?.AU?.latest_bar_date;
  const usLatest  = ingestionStatus?.US?.latest_bar_date;
  const lastAU    = tabDef.last_run_candle_date_au;
  const lastUS    = tabDef.last_run_candle_date_us;
  if((needsAU&&auStatus==='running')||(needsUS&&usStatus==='running')) return { type:'fetching', message:'Market data is currently being updated…' };
  const neverRun = (needsAU&&!lastAU)||(needsUS&&!lastUS);
  if(neverRun) return { type:'stale', message:'This scan has never been run.' };
  const auStale = needsAU && auLatest && lastAU !== auLatest;
  const usStale = needsUS && usLatest && lastUS !== usLatest;
  if(auStale||usStale) {
    const parts=[];
    if(auStale) parts.push(`AU: scanned ${lastAU}, latest ${auLatest}`);
    if(usStale) parts.push(`US: scanned ${lastUS}, latest ${usLatest}`);
    return { type:'stale', message:`New market data available. ${parts.join(' | ')}` };
  }
  return null;
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }) {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} />
      <span className="toggle-slider" />
    </label>
  );
}

function ParamInput({ label, value, onChange, min, max, step=1, width='70px', unit }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace"}}>
      {label}
      <input type="number" min={min} max={max} step={step} value={value} onChange={e=>onChange(e.target.value)}
        style={{width,padding:'3px 6px',fontSize:'12px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontFamily:"'DM Mono',monospace"}} />
      {unit&&<span style={{fontSize:'11px'}}>{unit}</span>}
    </label>
  );
}

function ParamSelect({ label, value, onChange, options }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace"}}>
      {label}
      <select value={value} onChange={e=>onChange(e.target.value)} style={{padding:'3px 6px',fontSize:'12px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontFamily:"'DM Mono',monospace"}}>
        {options.map(([v,l])=><option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

// ── Summary chips ─────────────────────────────────────────────────────────────
function buildSummaryChips({ sf, mcapMode, mcapTierDefs, mcapExcluded, emaRules, priceEmaRules, barConds, patternScans }) {
  const chips = [];
  if(mcapMode==='range') {
    if(sf.mcap_min||sf.mcap_max) chips.push(`MCap ${sf.mcap_min?'$'+sf.mcap_min+'M':''}${sf.mcap_min&&sf.mcap_max?'–':''}${sf.mcap_max?'$'+sf.mcap_max+'M':sf.mcap_min?'+':''}`);
  } else if(mcapExcluded.size>0) {
    const tiers = mcapTierDefs==='us'?MCAP_TIERS_US:MCAP_TIERS_ASX;
    const active = tiers.filter(t=>!mcapExcluded.has(t.key)).map(t=>t.label);
    if(active.length) chips.push(`MCap: ${active.join(', ')}`);
  }
  if(sf.price_min||sf.price_max) chips.push(`Price ${sf.price_min?'≥$'+sf.price_min:''}${sf.price_min&&sf.price_max?' ':''} ${sf.price_max?'≤$'+sf.price_max:''}`.trim());
  if(sf.rsi_min)  chips.push(`RSI ≥${sf.rsi_min}`);
  if(sf.rsi_max)  chips.push(`RSI ≤${sf.rsi_max}`);
  if(sf.stoch_min||sf.stoch_max) chips.push(`Stoch ${sf.stoch_min||''}–${sf.stoch_max||''}`);
  if(sf.volume_min) chips.push(`Vol ≥${Number(sf.volume_min).toLocaleString()}`);
  if(sf.yield_min||sf.yield_max) chips.push(`Yield ${sf.yield_min||''}–${sf.yield_max||''}%`);
  if(sf.bb_position) chips.push(`BB: ${sf.bb_position.replace('_',' ')}`);
  if(sf.atr_pct_min) chips.push(`ATR ≥${sf.atr_pct_min}%`);
  if(sf.obv_direction) chips.push(`OBV ${sf.obv_direction} ${sf.obv_days}d`);
  emaRules.forEach(r=>chips.push(`EMA${r.ema1} ${r.dir} EMA${r.ema2}`));
  priceEmaRules.forEach(r=>chips.push(`Price ${r.dir} EMA${r.ema}`));
  if(barConds.vol_spike.enabled)          chips.push(`Vol spike >${barConds.vol_spike.pct}% ${barConds.vol_spike.days}d avg`);
  if(barConds.big_green.enabled)          chips.push(`Big green >${barConds.big_green.body_pct}% body`);
  if(barConds.range_expansion.enabled)    chips.push(`Range exp >${barConds.range_expansion.pct}%`);
  if(barConds.gap.enabled)                chips.push(`Gap ${barConds.gap.direction} >${barConds.gap.pct}%`);
  if(barConds.inside_outside_bar.enabled) chips.push(`${barConds.inside_outside_bar.type==='inside'?'Inside':'Outside'} bar`);
  if(barConds.n_day_breakout.enabled)     chips.push(`${barConds.n_day_breakout.days}d ${barConds.n_day_breakout.direction} breakout`);
  if(barConds.week52_proximity.enabled)   chips.push(`Within ${barConds.week52_proximity.pct_from}% of 52w high`);
  if(barConds.consecutive_closes.enabled) chips.push(`${barConds.consecutive_closes.count} consec closes ${barConds.consecutive_closes.direction}`);
  if(barConds.ema_slope.enabled)          chips.push(`EMA${barConds.ema_slope.ema} ${barConds.ema_slope.direction} ${barConds.ema_slope.days}d`);
  patternScans.forEach(p => {
    const label = SCAN_TYPE_LABELS[p.scan_type] || p.scan_type;
    chips.push(label);
  });
  return chips;
}

function countMetrics({ sf, mcapMode, mcapExcluded, emaRules, priceEmaRules }) {
  let n = 0;
  if(mcapMode==='range'&&(sf.mcap_min||sf.mcap_max)) n++;
  if(mcapMode==='tiers'&&mcapExcluded.size>0) n++;
  if(sf.price_min||sf.price_max) n++;
  if(sf.rsi_min) n++;
  if(sf.rsi_max) n++;
  if(sf.stoch_min||sf.stoch_max) n++;
  if(sf.volume_min) n++;
  if(sf.yield_min||sf.yield_max) n++;
  if(sf.bb_position) n++;
  if(sf.atr_pct_min) n++;
  if(sf.obv_direction) n++;
  n += emaRules.length;
  n += priceEmaRules.length;
  return n;
}

function countBarConds(barConds) {
  return Object.values(barConds).filter(v=>v.enabled).length;
}

// ── Populate modal state from existing rules_json ─────────────────────────────
function rulesJsonToModalState(rules) {
  const sf   = rules.snapshot_filters || {};
  const bc   = rules.bar_conditions   || {};
  const ps   = rules.pattern_scans    || [];
  const exch = rules.exchanges        || ['AU'];

  const mcapTiers   = sf.mcap_tiers || [];
  const mcapMode    = mcapTiers.length > 0 ? 'tiers' : 'range';
  const mcapTierDefs= sf.mcap_tier_defs || 'us';
  const allTiers    = mcapTierDefs === 'asx' ? MCAP_TIERS_ASX : MCAP_TIERS_US;
  const mcapExcluded= mcapMode === 'tiers'
    ? new Set(allTiers.filter(t => !mcapTiers.includes(t.key)).map(t => t.key))
    : new Set();

  const barConds = defaultBarConditions();
  Object.entries(bc).forEach(([k, v]) => {
    if (barConds[k]) barConds[k] = { ...barConds[k], ...v };
  });

  const sfState = {
    mcap_min:      sf.mcap_min != null ? String(sf.mcap_min) : '',
    mcap_max:      sf.mcap_max != null ? String(sf.mcap_max) : '',
    price_min:     sf.price_min != null ? String(sf.price_min) : '',
    price_max:     sf.price_max != null ? String(sf.price_max) : '',
    rsi_min:       sf.rsi_min != null ? String(sf.rsi_min) : '',
    rsi_max:       sf.rsi_max != null ? String(sf.rsi_max) : '',
    stoch_min:     sf.stoch_min != null ? String(sf.stoch_min) : '',
    stoch_max:     sf.stoch_max != null ? String(sf.stoch_max) : '',
    volume_min:    sf.volume_min != null ? String(sf.volume_min) : '',
    yield_min:     sf.yield_min != null ? String(sf.yield_min) : '',
    yield_max:     sf.yield_max != null ? String(sf.yield_max) : '',
    bb_position:   sf.bb_position || '',
    atr_pct_min:   sf.atr_pct_min != null ? String(sf.atr_pct_min) : '',
    obv_direction: sf.obv_direction || '',
    obv_days:      sf.obv_days != null ? sf.obv_days : 10,
  };

  let ruleIdCounter = 1;
  const emaRules = (sf.ema_rules || []).map(r => ({ ...r, id: ruleIdCounter++ }));
  const priceEmaRules = (sf.price_ema_rules || []).map(r => ({ ...r, id: ruleIdCounter++ }));

  return {
    exchanges: exch,
    mcapMode,
    mcapTierDefs,
    mcapExcluded,
    sf: sfState,
    emaRules,
    priceEmaRules,
    barConds,
    patternScans: ps,
    patternOp: rules.pattern_operator || 'OR',
    ruleIdStart: ruleIdCounter,
  };
}

// ── ScanSubTable ──────────────────────────────────────────────────────────────
function ScanSubTable({ scanRows }) {
  const [sort, setSort] = useState({ col:'pattern_start_date', dir:'DESC' });
  const sorted = useMemo(()=>sortScanRows(scanRows, sort.col, sort.dir),[scanRows, sort]);
  const si = (col) => sort.col===col?(sort.dir==='ASC'?' ▲':' ▼'):' ⇅';
  return (
    <tr>
      <td colSpan={100} style={{padding:0, borderBottom:'2px solid var(--border)'}}>
        <div style={{background:'#f0f4ff', paddingLeft:'44px'}}>
          <table style={S.subTable}>
            <thead>
              <tr>{SCAN_SUB_COLS.map(c=><th key={c.key} style={S.subTh} onClick={()=>setSort(p=>({col:c.key,dir:p.col===c.key&&p.dir==='ASC'?'DESC':'ASC'}))}>{c.label}{si(c.key)}</th>)}</tr>
            </thead>
            <tbody>
              {sorted.map((sr,i)=>{
                const intact=sr.status==='intact';
                return (
                  <tr key={i} style={{background:i%2===0?'#f0f4ff':'#e8eeff'}}>
                    <td style={S.subTd}>{SCAN_TYPE_LABELS[sr.scan_type]||sr.scan_type||'-'}</td>
                    <td style={S.subTd}>{sr.pattern_start_date||'-'}</td>
                    <td style={S.subTd}>{sr.pattern_end_date||'-'}</td>
                    <td style={S.subTd}>{sr.trigger_date||'-'}</td>
                    <td style={S.subTd}>{fmtPrice(sr.target_price)}</td>
                    <td style={S.subTd}><span style={{...S.badge,background:intact?'#f0fdf4':'#fef2f2',color:intact?'#15803d':'#dc2626'}}>{sr.status||'-'}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

// ── ColFilterDropdown ─────────────────────────────────────────────────────────
function ColFilterDropdown({ values, excluded, onToggle, onClear, onClose }) {
  const ref = useRef(null);
  useEffect(()=>{
    const h=(e)=>{ if(ref.current&&!ref.current.contains(e.target))onClose(); };
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[onClose]);
  const sorted=[...values].sort();
  const allOn=excluded.size===0;
  return (
    <div ref={ref} style={S.colDropdown} onClick={e=>e.stopPropagation()}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'4px 12px 8px',borderBottom:'1px solid var(--border)',marginBottom:'4px'}}>
        <span style={{fontSize:'11px',fontWeight:700,color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",textTransform:'uppercase'}}>Filter</span>
        <div style={{display:'flex',gap:'6px'}}>
          <button style={{...S.btnGhost,padding:'2px 8px',fontSize:'11px'}} onClick={()=>allOn?onToggle('__DESELECT_ALL__',sorted):onClear()}>{allOn?'Deselect All':'Select All'}</button>
          {!allOn&&<button style={{...S.btnGhost,padding:'2px 8px',fontSize:'11px'}} onClick={onClear}>Clear</button>}
        </div>
      </div>
      <div style={{maxHeight:'260px',overflowY:'auto'}}>
        {sorted.map(v=>{
          const checked=!excluded.has(v);
          return (
            <label key={v} style={{...S.colDropItem,background:checked?'transparent':'#fff1f1'}}>
              <input type="checkbox" checked={checked} onChange={()=>onToggle(v)} style={{cursor:'pointer'}} />
              <span style={{color:checked?'var(--text)':'var(--text-muted)'}}>{v||'(empty)'}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab strips ────────────────────────────────────────────────────────────────
function makeTabStrip(onClose) {
  return function TabStrip({ tabs, activeTab, onSelect, onRefresh }) {
    const innerRef=useRef(null);
    const [canLeft,setCanLeft]=useState(false);
    const [canRight,setCanRight]=useState(false);
    const check=useCallback(()=>{
      const el=innerRef.current; if(!el)return;
      setCanLeft(el.scrollLeft>0);
      setCanRight(el.scrollLeft+el.clientWidth<el.scrollWidth-1);
    },[]);
    useEffect(()=>{
      check();
      const el=innerRef.current; if(!el)return;
      el.addEventListener('scroll',check,{passive:true});
      const ro=new ResizeObserver(check); ro.observe(el);
      return()=>{ el.removeEventListener('scroll',check); ro.disconnect(); };
    },[check]);
    const scroll=(d)=>{ const el=innerRef.current; if(el)el.scrollBy({left:d*SCROLL_STEP,behavior:'smooth'}); };
    return (
      <div style={S.tabStripOuter}>
        {canLeft&&<button style={S.scrollArrow} onClick={()=>scroll(-1)}>‹</button>}
        <div ref={innerRef} style={S.tabStripInner}>
          {tabs.map(t=>(
            <div key={t.id} style={S.tab(activeTab===t.id)} onClick={()=>onSelect(t.id)}>
              <span>{t.label}</span>
              {onRefresh&&<button style={S.tabBtn} onClick={e=>{e.stopPropagation();onRefresh(t.id);}} title="Refresh">↺</button>}
              <button style={S.tabBtn} onClick={e=>{e.stopPropagation();onClose(t.id);}} title="Close">×</button>
            </div>
          ))}
        </div>
        {canRight&&<button style={S.scrollArrow} onClick={()=>scroll(1)}>›</button>}
      </div>
    );
  };
}

// ── ScanDetailsModal ──────────────────────────────────────────────────────────
function ScanDetailsModal({ tab, onClose }) {
  const rules = tab.rules_json || {};
  const sf    = rules.snapshot_filters || {};
  const ps    = rules.pattern_scans || [];
  const bc    = rules.bar_conditions || {};
  return (
    <div style={S.modalOverlay} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={S.modalSm}>
        <div style={S.modalTitle}>{tab.tab_name}</div>
        {tab.description&&<div style={S.modalSub}>{tab.description}</div>}
        <div style={S.detailRow}><span style={S.detailKey}>Exchanges</span><span style={S.detailVal}>{(rules.exchanges||[]).join(', ')||'AU + US'}</span></div>
        {ps.length>0&&(
          <div style={S.detailRow}>
            <span style={S.detailKey}>Pattern Scans</span>
            <span style={S.detailVal}>
              {ps.map((p,i)=>(
                <div key={i} style={{marginBottom:'6px'}}>
                  <span style={S.detailChip}>{SCAN_TYPE_LABELS[p.scan_type]||p.scan_type}</span>
                </div>
              ))}
              {ps.length>1&&<div style={{fontSize:'11px',color:'var(--text-muted)'}}>Combined with: {rules.pattern_operator||'OR'}</div>}
            </span>
          </div>
        )}
        {sf.mcap_min!=null&&<div style={S.detailRow}><span style={S.detailKey}>Market Cap</span><span style={S.detailVal}>${sf.mcap_min}M – {sf.mcap_max!=null?`$${sf.mcap_max}M`:'∞'}</span></div>}
        {sf.price_min!=null&&<div style={S.detailRow}><span style={S.detailKey}>Price</span><span style={S.detailVal}>${sf.price_min} – {sf.price_max!=null?`$${sf.price_max}`:'∞'}</span></div>}
        {sf.rsi_min!=null&&<div style={S.detailRow}><span style={S.detailKey}>RSI min</span><span style={S.detailVal}>{sf.rsi_min}</span></div>}
        {sf.rsi_max!=null&&<div style={S.detailRow}><span style={S.detailKey}>RSI max</span><span style={S.detailVal}>{sf.rsi_max}</span></div>}
        {sf.volume_min!=null&&<div style={S.detailRow}><span style={S.detailKey}>Min Volume</span><span style={S.detailVal}>{Number(sf.volume_min).toLocaleString()}</span></div>}
        {sf.ema_rules?.length>0&&<div style={S.detailRow}><span style={S.detailKey}>EMA Rules</span><span style={S.detailVal}>{sf.ema_rules.map((r,i)=><div key={i}>EMA {r.ema1} {r.dir} EMA {r.ema2}</div>)}</span></div>}
        {sf.price_ema_rules?.length>0&&<div style={S.detailRow}><span style={S.detailKey}>Price vs EMA</span><span style={S.detailVal}>{sf.price_ema_rules.map((r,i)=><div key={i}>Price {r.dir} EMA {r.ema}</div>)}</span></div>}
        {Object.values(bc).some(v=>v.enabled)&&(
          <div style={S.detailRow}>
            <span style={S.detailKey}>Bar Conditions</span>
            <span style={S.detailVal}>{Object.entries(bc).filter(([,v])=>v.enabled).map(([k])=><span key={k} style={S.detailChip}>{k.replace(/_/g,' ')}</span>)}</span>
          </div>
        )}
        <div style={S.detailRow}><span style={S.detailKey}>Last Run AU</span><span style={S.detailVal}>{tab.last_run_candle_date_au||'Never'}</span></div>
        <div style={S.detailRow}><span style={S.detailKey}>Last Run US</span><span style={S.detailVal}>{tab.last_run_candle_date_us||'Never'}</span></div>
        {tab.last_run_count!=null&&<div style={S.detailRow}><span style={S.detailKey}>Last Result Count</span><span style={S.detailVal}>{tab.last_run_count.toLocaleString()}</span></div>}
        <div style={S.modalFooter}><button style={S.btnGhost} onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

// ── DeleteConfirmModal ────────────────────────────────────────────────────────
function DeleteConfirmModal({ tab, onClose, onConfirm, busy }) {
  return (
    <div style={S.modalOverlay} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{...S.modalSm,maxWidth:'400px'}}>
        <div style={S.modalTitle}>Delete Scan</div>
        <div style={{...S.modalSub,marginBottom:'24px'}}>
          Are you sure you want to delete <strong>{tab.tab_name}</strong>? This will permanently remove the scan and all its results.
        </div>
        <div style={S.modalFooter}>
          <button style={S.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={S.btnDanger} onClick={onConfirm} disabled={busy}>{busy?'Deleting…':'Delete Scan'}</button>
        </div>
      </div>
    </div>
  );
}

// ── PatternBlock ──────────────────────────────────────────────────────────────
function PatternBlock({ pattern, onChange, onRemove, activeHelp, setActiveHelp }) {
  const set = (k,v) => onChange({...pattern,[k]:v});
  const label = SCAN_TYPE_LABELS[pattern.scan_type] || pattern.scan_type;
  const helpText = PATTERN_HELP[pattern.scan_type] || '';
  const isHelpOpen = helpText && activeHelp === helpText;

  return (
    <div style={S.patternBlock}>
      {/* Header row */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
          <span style={S.patternTitle}>{label}</span>
          {helpText && (
            <button
              onClick={()=>setActiveHelp(isHelpOpen ? '' : helpText)}
              style={{
                background:'none', border:'none', cursor:'pointer', fontSize:'13px',
                padding:'0 2px', lineHeight:1,
                color: isHelpOpen ? 'var(--accent)' : 'var(--text-muted)',
                opacity: isHelpOpen ? 1 : 0.6,
                fontWeight: isHelpOpen ? 700 : 400,
              }}
              title={isHelpOpen ? 'Hide help' : 'Show help'}
            >ⓘ</button>
          )}
        </div>
        <button style={S.removeBtn} onClick={onRemove}>✕ Remove</button>
      </div>
      {/* ── Params per scan type ── */}
      {pattern.scan_type==='resistance'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Touch Type</span><select style={S.filterSelect} value={pattern.touch_type} onChange={e=>set('touch_type',e.target.value)}><option value="body">Body (open/close tops)</option><option value="wick">Wick (bar highs)</option><option value="both">Both</option></select></div>
          <div><span style={S.filterLabel}>Lookback (bars)</span><input style={S.filterInput} type="number" min={50} max={500} value={pattern.candles} onChange={e=>set('candles',parseInt(e.target.value)||200)} /></div>
          <div><span style={S.filterLabel}>Min Touches</span><input style={S.filterInput} type="number" min={2} max={20} value={pattern.min_touches} onChange={e=>set('min_touches',parseInt(e.target.value)||7)} /></div>
          <div><span style={S.filterLabel}>Recent (bars)</span><input style={S.filterInput} type="number" min={1} max={50} value={pattern.recent_bars} onChange={e=>set('recent_bars',parseInt(e.target.value)||10)} /></div>
        </div>
      )}

      {pattern.scan_type==='trendline'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Min Touches</span><input style={S.filterInput} type="number" min={2} max={20} value={pattern.min_touches} onChange={e=>set('min_touches',parseInt(e.target.value)||4)} /></div>
          <div><span style={S.filterLabel}>Touch Tolerance %</span><input style={S.filterInput} type="number" min={0.1} max={10} step={0.1} value={pattern.touch_pct} onChange={e=>set('touch_pct',parseFloat(e.target.value)||1.5)} /></div>
          <div><span style={S.filterLabel}>Swing Order (bars)</span><input style={S.filterInput} type="number" min={1} max={20} value={pattern.swing_order} onChange={e=>set('swing_order',parseInt(e.target.value)||5)} /></div>
          <div><span style={S.filterLabel}>Min Decline %</span><input style={S.filterInput} type="number" min={1} max={100} step={0.5} value={pattern.min_decline_pct} onChange={e=>set('min_decline_pct',parseFloat(e.target.value)||15)} /></div>
          <div><span style={S.ilterLabel}>Breakout Window (days)</span><input style={S.filterInput} type="number" min={1} max={60} value={pattern.breakout_days} onChange={e=>set('breakout_days',parseInt(e.target.value)||10)} /></div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {pattern.scan_type==='ascending_trendline'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Min Touches</span><input style={S.filterInput} type="number" min={2} max={20} value={pattern.min_touches} onChange={e=>set('min_touches',parseInt(e.target.value)||4)} /></div>
          <div><span style={S.filterLabel}>Touch Tolerance %</span><input style={S.filterInput} type="number" min={0.1} max={10} step={0.1} value={pattern.touch_pct} onChange={e=>set('touch_pct',parseFloat(e.target.value)||1.5)} /></div>
          <div><span style={S.filterLabel}>Swing Order (bars)</span><input style={S.filterInput} type="number" min={1} max={20} value={pattern.swing_order} onChange={e=>set('swing_order',parseInt(e.target.value)||5)} /></div>
          <div><span style={S.filterLabel}>Min Rise %</span><input style={S.filterInput} type="number" min={1} max={100} step={0.5} value={pattern.min_rise_pct} onChange={e=>set('min_rise_pct',parseFloat(e.target.value)||10)} /></div>
          <div><span style={S.filterLabel}>Breakout Window (days)</span><input style={S.filterInput} type="number" min={1} max={60} value={pattern.breakout_days} onChange={e=>set('breakout_days',parseInt(e.target.value)||10)} /></div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {pattern.scan_type==='support'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Touch Type</span><select style={S.filterSelect} value={pattern.touch_type} onChange={e=>set('touch_type',e.target.value)}><option value="wick">Wick (bar lows)</option><option value="body">Body (open/close bottoms)</option><option value="both">Both</option></select></div>
          <div><span style={S.filterLabel}>Lookback (bars)</span><input style={S.filterInput} type="number" min={50} max={500} value={pattern.candles} onChange={e=>set('candles',parseInt(e.target.value)||200)} /></div>
          <div><span style={S.filterLabel}>Min Touches</span><input style={S.filterInput} type="number" min={2} max={20} value={pattern.min_touches} onChange={e=>set('min_touches',parseInt(e.target.value)||5)} /></div>
          <div><span style={S.filterLabel}>Recent (bars)</span><input style={S.filterInput} type="number" min={1} max={50} value={pattern.recent_bars} onChange={e=>set('recent_bars',parseInt(e.target.value)||10)} /></div>
        </div>
      )}

      {(pattern.scan_type==='falling_wedge'||pattern.scan_type==='rising_wedge')&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Min Touches (each line)</span><input style={S.filterInput} type="number" min={2} max={10} value={pattern.min_touches} onChange={e=>set('min_touches',parseInt(e.target.value)||3)} /></div>
          <div><span style={S.filterLabel}>Touch Tolerance %</span><input style={S.filterInput} type="number" min={0.1} max={10} step={0.1} value={pattern.touch_pct} onChange={e=>set('touch_pct',parseFloat(e.target.value)||1.5)} /></div>
          <div><span style={S.filterLabel}>Swing Order (bars)</span><input style={S.filterInput} type="number" min={1} max={20} value={pattern.swing_order} onChange={e=>set('swing_order',parseInt(e.target.value)||5)} /></div>
          <div><span style={S.filterLabel}>{pattern.scan_type==='falling_wedge'?'Min Decline %':'Min Rise %'}</span><input style={S.filterInput} type="number" min={1} max={100} step={0.5} value={pattern.scan_type==='falling_wedge'?pattern.min_decline_pct:pattern.min_rise_pct} onChange={e=>set(pattern.scan_type==='falling_wedge'?'min_decline_pct':'min_rise_pct',parseFloat(e.target.value)||10)} /></div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {(pattern.scan_type==='ascending_triangle'||pattern.scan_type==='descending_triangle')&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>{pattern.scan_type==='ascending_triangle'?'Resistance':'Support'} Tol %</span><input style={S.filterInput} type="number" min={0.1} max={10} step={0.1} value={pattern.scan_type==='ascending_triangle'?pattern.resistance_tol_pct:pattern.support_tol_pct} onChange={e=>set(pattern.scan_type==='ascending_triangle'?'resistance_tol_pct':'support_tol_pct',parseFloat(e.target.value)||1.5)} /></div>
          <div><span style={S.filterLabel}>Min Flat Touches</span><input style={S.filterInput} type="number" min={2} max={10} value={pattern.scan_type==='ascending_triangle'?pattern.min_resistance_touches:pattern.min_support_touches} onChange={e=>set(pattern.scan_type==='ascending_triangle'?'min_resistance_touches':'min_support_touches',parseInt(e.target.value)||3)} /></div>
          <div><span style={S.filterLabel}>Trendline Touches</span><input style={S.filterInput} type="number" min={2} max={10} value={pattern.trendline_touches} onChange={e=>set('trendline_touches',parseInt(e.target.value)||3)} /></div>
          <div><span style={S.filterLabel}>Touch Tolerance %</span><input style={S.filterInput} type="number" min={0.1} max={10} step={0.1} value={pattern.touch_pct} onChange={e=>set('touch_pct',parseFloat(e.target.value)||1.5)} /></div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {(pattern.scan_type==='double_top'||pattern.scan_type==='double_bottom')&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>{pattern.scan_type==='double_top'?'Peak':'Trough'} Tolerance %</span><input style={S.filterInput} type="number" min={0.5} max={15} step={0.5} value={pattern.scan_type==='double_top'?pattern.peak_tol_pct:pattern.trough_tol_pct} onChange={e=>set(pattern.scan_type==='double_top'?'peak_tol_pct':'trough_tol_pct',parseFloat(e.target.value)||3)} /></div>
          <div><span style={S.filterLabel}>Min Sep (bars)</span><input style={S.filterInput} type="number" min={5} max={60} value={pattern.min_sep_bars} onChange={e=>set('min_sep_bars',parseInt(e.target.value)||10)} /></div>
          <div><span style={S.filterLabel}>Max Sep (bars)</span><input style={S.filterInput} type="number" min={20} max={300} value={pattern.max_sep_bars} onChange={e=>set('max_sep_bars',parseInt(e.target.value)||120)} /></div>
          <div style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'16px'}}>
            <input type="checkbox" checked={pattern.require_neckline_break} onChange={e=>set('require_neckline_break',e.target.checked)} id={`nkb-${pattern.scan_type}`} />
            <label htmlFor={`nkb-${pattern.scan_type}`} style={{fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",cursor:'pointer'}}>Require neckline break</label>
          </div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {pattern.scan_type==='vdu_tightening'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Contraction Bars</span><input style={S.filterInput} type="number" min={3} max={20} value={pattern.contraction_bars} onChange={e=>set('contraction_bars',parseInt(e.target.value)||5)} /></div>
          <div><span style={S.filterLabel}>Max Range Expansion %</span><input style={S.filterInput} type="number" min={1} max={50} value={pattern.max_range_expansion_pct} onChange={e=>set('max_range_expansion_pct',parseFloat(e.target.value)||10)} /></div>
          <div style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'16px'}}>
            <input type="checkbox" checked={pattern.volume_declining} onChange={e=>set('volume_declining',e.target.checked)} id="vdu-vol" />
            <label htmlFor="vdu-vol" style={{fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",cursor:'pointer'}}>Volume must also decline</label>
          </div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={30} max={365} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||180)} /></div>
        </div>
      )}

      {pattern.scan_type==='cup_and_handle'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Rim Tolerance %</span><input style={S.filterInput} type="number" min={1} max={15} step={0.5} value={pattern.rim_tolerance_pct} onChange={e=>set('rim_tolerance_pct',parseFloat(e.target.value)||5)} /></div>
          <div><span style={S.filterLabel}>Min Cup Bars</span><input style={S.filterInput} type="number" min={5} max={200} value={pattern.cup_min_bars} onChange={e=>set('cup_min_bars',parseInt(e.target.value)||30)} /></div>
          <div><span style={S.filterLabel}>Min Cup Depth %</span><input style={S.filterInput} type="number" min={5} max={40} value={pattern.cup_depth_min_pct} onChange={e=>set('cup_depth_min_pct',parseFloat(e.target.value)||12)} /></div>
          <div><span style={S.filterLabel}>Max Cup Depth %</span><input style={S.filterInput} type="number" min={10} max={70} value={pattern.cup_depth_max_pct} onChange={e=>set('cup_depth_max_pct',parseFloat(e.target.value)||50)} /></div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={180} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||500)} /></div>
          <div style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'16px'}}>
            <input type="checkbox" checked={pattern.prior_uptrend} onChange={e=>set('prior_uptrend',e.target.checked)} id="cup-uptrend" />
            <label htmlFor="cup-uptrend" style={{fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",cursor:'pointer'}}>Require prior uptrend (EMA20 &gt; EMA50)</label>
          </div>
        </div>
      )}

      {pattern.scan_type==='darvas_box'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Confirm Bars</span><input style={S.filterInput} type="number" min={2} max={10} value={pattern.confirm_bars} onChange={e=>set('confirm_bars',parseInt(e.target.value)||3)} /></div>
          <div><span style={S.filterLabel}>Min Box Bars</span><input style={S.filterInput} type="number" min={3} max={40} value={pattern.min_box_bars} onChange={e=>set('min_box_bars',parseInt(e.target.value)||5)} /></div>
          <div><span style={S.filterLabel}>Max Box Depth %</span><input style={S.filterInput} type="number" min={2} max={40} value={pattern.max_box_depth_pct} onChange={e=>set('max_box_depth_pct',parseFloat(e.target.value)||15)} /></div>
          <div style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'16px'}}>
            <input type="checkbox" checked={pattern.vol_spike_on_break} onChange={e=>set('vol_spike_on_break',e.target.checked)} id="darvas-vol" />
            <label htmlFor="darvas-vol" style={{fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",cursor:'pointer'}}>Require volume spike on breakout</label>
          </div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {(pattern.scan_type==='bull_flag'||pattern.scan_type==='bear_flag')&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Pole Min {pattern.scan_type==='bull_flag'?'Gain':'Decline'} %</span><input style={S.filterInput} type="number" min={5} max={200} value={pattern.scan_type==='bull_flag'?pattern.pole_min_gain_pct:pattern.pole_min_decline_pct} onChange={e=>set(pattern.scan_type==='bull_flag'?'pole_min_gain_pct':'pole_min_decline_pct',parseFloat(e.target.value)||15)} /></div>
          <div><span style={S.filterLabel}>Pole Max Bars</span><input style={S.filterInput} type="number" min={3} max={60} value={pattern.pole_max_bars} onChange={e=>set('pole_max_bars',parseInt(e.target.value)||20)} /></div>
          <div><span style={S.filterLabel}>Flag Max {pattern.scan_type==='bull_flag'?'Depth':'Rally'} %</span><input style={S.filterInput} type="number" min={5} max={80} value={pattern.scan_type==='bull_flag'?pattern.flag_max_depth_pct:pattern.flag_max_rally_pct} onChange={e=>set(pattern.scan_type==='bull_flag'?'flag_max_depth_pct':'flag_max_rally_pct',parseFloat(e.target.value)||50)} /></div>
          <div><span style={S.filterLabel}>Flag Max Bars</span><input style={S.filterInput} type="number" min={3} max={60} value={pattern.flag_max_bars} onChange={e=>set('flag_max_bars',parseInt(e.target.value)||25)} /></div>
          <div style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'16px'}}>
            <input type="checkbox" checked={pattern.volume_dryup} onChange={e=>set('volume_dryup',e.target.checked)} id={`flag-vol-${pattern.scan_type}`} />
            <label htmlFor={`flag-vol-${pattern.scan_type}`} style={{fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",cursor:'pointer'}}>Volume dry-up during flag</label>
          </div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={30} max={365} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||180)} /></div>
        </div>
      )}

      {pattern.scan_type==='bull_pennant'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Pole Min Gain %</span><input style={S.filterInput} type="number" min={5} max={200} value={pattern.pole_min_gain_pct} onChange={e=>set('pole_min_gain_pct',parseFloat(e.target.value)||15)} /></div>
          <div><span style={S.filterLabel}>Pole Max Bars</span><input style={S.filterInput} type="number" min={3} max={60} value={pattern.pole_max_bars} onChange={e=>set('pole_max_bars',parseInt(e.target.value)||20)} /></div>
          <div><span style={S.filterLabel}>Pennant Max Bars</span><input style={S.filterInput} type="number" min={3} max={60} value={pattern.pennant_max_bars} onChange={e=>set('pennant_max_bars',parseInt(e.target.value)||20)} /></div>
          <div style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'16px'}}>
            <input type="checkbox" checked={pattern.volume_dryup} onChange={e=>set('volume_dryup',e.target.checked)} id="pennant-vol" />
            <label htmlFor="pennant-vol" style={{fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",cursor:'pointer'}}>Volume dry-up during pennant</label>
          </div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={30} max={365} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||180)} /></div>
        </div>
      )}

      {(pattern.scan_type==='head_and_shoulders'||pattern.scan_type==='inv_head_and_shoulders')&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Shoulder Symmetry %</span><input style={S.filterInput} type="number" min={1} max={30} value={pattern.shoulder_sym_pct} onChange={e=>set('shoulder_sym_pct',parseFloat(e.target.value)||10)} /></div>
          <div><span style={S.filterLabel}>{pattern.scan_type==='head_and_shoulders'?'Head Premium %':'Head Discount %'}</span><input style={S.filterInput} type="number" min={1} max={30} value={pattern.scan_type==='head_and_shoulders'?pattern.head_premium_pct:pattern.head_discount_pct} onChange={e=>set(pattern.scan_type==='head_and_shoulders'?'head_premium_pct':'head_discount_pct',parseFloat(e.target.value)||3)} /></div>
          <div style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'16px'}}>
            <input type="checkbox" checked={pattern.require_neckline_break} onChange={e=>set('require_neckline_break',e.target.checked)} id={`hs-nkb-${pattern.scan_type}`} />
            <label htmlFor={`hs-nkb-${pattern.scan_type}`} style={{fontSize:'12px',color:'var(--text-muted)',fontFamily:"'DM Mono',monospace",cursor:'pointer'}}>Require neckline break</label>
          </div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {pattern.scan_type==='high_tight_flag'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Pole Min Gain %</span><input style={S.filterInput} type="number" min={50} max={500} value={pattern.pole_min_gain_pct} onChange={e=>set('pole_min_gain_pct',parseFloat(e.target.value)||100)} /></div>
          <div><span style={S.filterLabel}>Pole Max Bars</span><input style={S.filterInput} type="number" min={5} max={60} value={pattern.pole_max_bars} onChange={e=>set('pole_max_bars',parseInt(e.target.value)||40)} /></div>
          <div><span style={S.filterLabel}>Flag Max Depth %</span><input style={S.filterInput} type="number" min={3} max={50} value={pattern.flag_max_depth_pct} onChange={e=>set('flag_max_depth_pct',parseFloat(e.target.value)||25)} /></div>
          <div><span style={S.filterLabel}>Flag Max Bars</span><input style={S.filterInput} type="number" min={3} max={60} value={pattern.flag_max_bars} onChange={e=>set('flag_max_bars',parseInt(e.target.value)||25)} /></div>
          <div><span style={S.filterLabel}>Lookback (days)</span><input style={S.filterInput} type="number" min={90} max={1000} value={pattern.lookback_days} onChange={e=>set('lookback_days',parseInt(e.target.value)||365)} /></div>
        </div>
      )}

      {pattern.scan_type==='engulfing'&&(
        <div style={S.filterGrid}>
          <div><span style={S.filterLabel}>Direction</span><select style={S.filterSelect} value={pattern.direction} onChange={e=>set('direction',e.target.value)}><option value="bullish">Bullish</option><option value="bearish">Bearish</option><option value="either">Either</option></select></div>
          <div><span style={S.filterLabel}>Within Last (days)</span><input style={S.filterInput} type="number" min={0} max={60} value={pattern.days_ago} onChange={e=>set('days_ago',Math.max(0,parseInt(e.target.value)||0))} /></div>
        </div>
      )}
    </div>
  );
}

// ── AddPatternDropdown ────────────────────────────────────────────────────────
function AddPatternDropdown({ onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(()=>{
    const h=(e)=>{ if(ref.current&&!ref.current.contains(e.target))setOpen(false); };
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);
  return (
    <div style={{position:'relative'}} ref={ref}>
      <button style={S.addRuleBtn} onClick={()=>setOpen(p=>!p)}>
        + Add Pattern ▾
      </button>
      {open&&(
        <div className="pattern-menu-dropdown">
          {PATTERN_MENU.map(m=>(
            <div key={m.scan_type} className="pattern-menu-item"
              onClick={()=>{ onAdd(m.scan_type); setOpen(false); }}>
              {m.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── NewScanModal (also used for Edit) ─────────────────────────────────────────
function NewScanModal({ onClose, onCreated, editDef }) {
  const isEdit = !!editDef;

  const init = useMemo(() => editDef ? rulesJsonToModalState(editDef.rules_json || {}) : null, [editDef]);

  const [label,        setLabel]        = useState(isEdit ? editDef.label || '' : '');
  const [description,  setDescription]  = useState(isEdit ? editDef.description || '' : '');
  const [exchanges,    setExchanges]    = useState(init?.exchanges || ['AU']);
  const [activeTab,    setActiveTab]    = useState('metrics');
  const [mcapMode,     setMcapMode]     = useState(init?.mcapMode || 'range');
  const [mcapTierDefs, setMcapTierDefs] = useState(init?.mcapTierDefs || 'us');
  const [mcapExcluded, setMcapExcluded] = useState(init?.mcapExcluded || new Set());
  const [sf, setSf] = useState(init?.sf || {
    mcap_min:'', mcap_max:'', price_min:'', price_max:'',
    rsi_min:'', rsi_max:'', stoch_min:'', stoch_max:'',
    volume_min:'', yield_min:'', yield_max:'',
    bb_position:'', atr_pct_min:'', obv_direction:'', obv_days:10,
  });
  const [emaRules,      setEmaRules]      = useState(init?.emaRules || []);
  const [priceEmaRules, setPriceEmaRules] = useState(init?.priceEmaRules || []);
  const [barConds,      setBarConds]      = useState(init?.barConds || defaultBarConditions());
  const [patternScans,  setPatternScans]  = useState(init?.patternScans || []);
  const [patternOp,     setPatternOp]     = useState(init?.patternOp || 'OR');
  const [busy,          setBusy]          = useState(false);
  const [error,         setError]         = useState('');

  const ruleId = useRef(init?.ruleIdStart || 1);
  const [helpText, setHelpText] = useState('');

  const setSfF = (k,v) => setSf(p=>({...p,[k]:v}));
  const setBC  = (key, patch) => setBarConds(p=>({...p,[key]:{...p[key],...patch}}));
  const toggleExchange = (ex) => setExchanges(p=>p.includes(ex)?p.filter(e=>e!==ex):[...p,ex]);

  const addPattern    = (scan_type) => {
    const factory = PATTERN_DEFAULTS[scan_type];
    if(factory) setPatternScans(p=>[...p,factory()]);
  };
  const removePattern = (i) => setPatternScans(p=>p.filter((_,idx)=>idx!==i));
  const updatePattern = (i,u) => setPatternScans(p=>p.map((x,idx)=>idx===i?u:x));

  const addEma = () => { const id=++ruleId.current; setEmaRules(p=>[...p,{id,ema1:'50',ema2:'200',dir:'above'}]); };
  const remEma = (id) => setEmaRules(p=>p.filter(r=>r.id!==id));
  const updEma = (id,k,v) => setEmaRules(p=>p.map(r=>r.id===id?{...r,[k]:v}:r));
  const addPr  = () => { const id=++ruleId.current; setPriceEmaRules(p=>[...p,{id,ema:'50',dir:'above'}]); };
  const remPr  = (id) => setPriceEmaRules(p=>p.filter(r=>r.id!==id));
  const updPr  = (id,k,v) => setPriceEmaRules(p=>p.map(r=>r.id===id?{...r,[k]:v}:r));

  const mcapTiers = mcapTierDefs==='us'?MCAP_TIERS_US:MCAP_TIERS_ASX;
  const metricsCount  = countMetrics({ sf, mcapMode, mcapExcluded, emaRules, priceEmaRules });
  const barCondCount  = countBarConds(barConds);
  const patternCount  = patternScans.length;
  const summaryChips  = buildSummaryChips({ sf, mcapMode, mcapTierDefs, mcapExcluded, emaRules, priceEmaRules, barConds, patternScans });

  const buildRules = () => {
    const snapshotFilters = {};
    if(mcapMode==='range') {
      if(sf.mcap_min) snapshotFilters.mcap_min = parseFloat(sf.mcap_min);
      if(sf.mcap_max) snapshotFilters.mcap_max = parseFloat(sf.mcap_max);
    } else {
      const activeTiers = mcapTiers.filter(t=>!mcapExcluded.has(t.key));
      if(activeTiers.length > 0 && activeTiers.length < mcapTiers.length) {
        snapshotFilters.mcap_tiers = activeTiers.map(t=>t.key);
        snapshotFilters.mcap_tier_defs = mcapTierDefs;
      }
    }
    if(sf.price_min)    snapshotFilters.price_min    = parseFloat(sf.price_min);
    if(sf.price_max)    snapshotFilters.price_max    = parseFloat(sf.price_max);
    if(sf.rsi_min)      snapshotFilters.rsi_min      = parseFloat(sf.rsi_min);
    if(sf.rsi_max)      snapshotFilters.rsi_max      = parseFloat(sf.rsi_max);
    if(sf.stoch_min)    snapshotFilters.stoch_min    = parseFloat(sf.stoch_min);
    if(sf.stoch_max)    snapshotFilters.stoch_max    = parseFloat(sf.stoch_max);
    if(sf.volume_min)   snapshotFilters.volume_min   = parseFloat(sf.volume_min);
    if(sf.yield_min)    snapshotFilters.yield_min    = parseFloat(sf.yield_min);
    if(sf.yield_max)    snapshotFilters.yield_max    = parseFloat(sf.yield_max);
    if(sf.bb_position)  snapshotFilters.bb_position  = sf.bb_position;
    if(sf.atr_pct_min)  snapshotFilters.atr_pct_min  = parseFloat(sf.atr_pct_min);
    if(sf.obv_direction) { snapshotFilters.obv_direction = sf.obv_direction; snapshotFilters.obv_days = parseInt(sf.obv_days)||10; }
    if(emaRules.length>0)      snapshotFilters.ema_rules       = emaRules.map(({id,...r})=>r);
    if(priceEmaRules.length>0) snapshotFilters.price_ema_rules = priceEmaRules.map(({id,...r})=>r);
    const bar_conditions = {};
    Object.entries(barConds).forEach(([k,v])=>{ if(v.enabled) bar_conditions[k]={...v}; });
    return {
      type: 'custom_scan',
      exchanges,
      snapshot_filters: snapshotFilters,
      bar_conditions: Object.keys(bar_conditions).length>0 ? bar_conditions : undefined,
      pattern_scans: patternScans,
      pattern_operator: patternOp,
    };
  };

  const validate = () => {
    if(!label.trim()) { setError('Name required'); return false; }
    if(exchanges.length===0) { setError('Select at least one exchange'); return false; }
    return true;
  };

  const handleSave = async () => {
    if(!validate()) return;
    setBusy(true); setError('');
    try {
      const r = await api.put(`/scans/definitions/${editDef.definition_id}`, {
        label: label.trim(),
        description: description.trim()||undefined,
        rules_json: buildRules(),
      });
      onCreated(r.data, 'saved');
      onClose();
    } catch(e) { setError(e.response?.data?.error||'Failed to save'); }
    finally { setBusy(false); }
  };

  const handleSaveAsNew = async () => {
    if(!validate()) return;
    setBusy(true); setError('');
    try {
      const r = await api.post('/scans/definitions', {
        label: label.trim(),
        description: description.trim()||undefined,
        rules_json: buildRules(),
        add_tab: true,
      });
      onCreated(r.data, 'created');
      onClose();
    } catch(e) { setError(e.response?.data?.error||'Failed to create scan'); }
    finally { setBusy(false); }
  };

  const TabBadge = ({count}) => <span className={`modal-tab-badge${count===0?' zero':''}`}>{count}</span>;

  return (
    <div style={S.modalOverlay} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={S.modal}>
        <div style={S.modalTitle}>{isEdit ? `Edit Scan — ${editDef.label}` : 'New Scan'}</div>
        <div style={S.modalSub}>
          {isEdit
            ? 'Update this scan\'s rules. Save will overwrite and clear existing results. Save as New creates a separate scan.'
            : 'Build a custom scan by combining metric filters, bar conditions, and chart patterns.'}
        </div>

        {/* Pinned: name / description / exchanges */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'14px'}}>
          <div>
            <label style={S.modalLabel}>Scan Name</label>
            <input style={S.modalInput} value={label} onChange={e=>setLabel(e.target.value)} placeholder="e.g. ASX Breakout Setup" maxLength={100} />
          </div>
          <div>
            <label style={S.modalLabel}>Description (optional)</label>
            <input style={S.modalInput} value={description} onChange={e=>setDescription(e.target.value)} placeholder="What does this scan look for?" />
          </div>
        </div>
        <div style={{marginBottom:'16px'}}>
          <label style={S.modalLabel}>Exchanges</label>
          <div style={{display:'flex',gap:'8px',marginTop:'4px'}}>
            {['AU','US'].map(ex=><button key={ex} style={S.colToggle(exchanges.includes(ex))} onClick={()=>toggleExchange(ex)}>{ex}</button>)}
          </div>
        </div>

        {/* Summary strip */}
        <div style={{borderTop:'1px solid var(--border)',borderBottom:'1px solid var(--border)',marginBottom:'0',padding:'0 0 2px'}}>
          <div className="summary-strip">
            {summaryChips.length===0
              ? <span className="summary-empty">No conditions set — scan will return all instruments on selected exchanges.</span>
              : summaryChips.map((c,i)=><span key={i} className="summary-chip">✓ {c}</span>)
            }
          </div>
        </div>

        {/* Help panel — fixed area above tabs, only visible when a ⓘ is active */}
        {helpText && (
          <div style={{
            borderTop:'1px solid var(--accent)',
            borderBottom:'1px solid var(--accent)',
            background:'var(--accent-dim, rgba(99,102,241,0.07))',
            padding:'10px 14px', maxHeight:'180px', overflowY:'auto',
            fontSize:'12px', color:'var(--text)', lineHeight:'1.65', whiteSpace:'pre-line',
          }}>
            {helpText}
          </div>
        )}

        {/* Modal inner tab bar */}
        <div style={{display:'flex',gap:'2px',borderBottom:'1px solid var(--border)',marginBottom:'16px',marginTop:'0',paddingTop:'10px'}}>
          {[
            { id:'metrics',     label:'Metrics',      count:metricsCount },
            { id:'priceaction', label:'Price Action',  count:barCondCount },
            { id:'patterns',    label:'Patterns',      count:patternCount },
          ].map(t=>(
            <button key={t.id} className={`modal-inner-tab${activeTab===t.id?' active':''}`} onClick={()=>{ setActiveTab(t.id); setHelpText(''); }}>
              {t.label}<TabBadge count={t.count} />
            </button>
          ))}
        </div>

        {/* ── METRICS TAB ─────────────────────────────────────────────────── */}
        {activeTab==='metrics'&&(
          <div style={{display:'flex',flexDirection:'column',gap:'18px'}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px'}}>
                <FieldLabel helpText={METRIC_HELP.market_cap} activeHelp={helpText} setActiveHelp={setHelpText}>Market Cap</FieldLabel>
                <button style={{...S.colToggle(mcapMode==='range'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>setMcapMode('range')}>Range</button>
                <button style={{...S.colToggle(mcapMode==='tiers'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>setMcapMode('tiers')}>Tiers</button>
              </div>
              {mcapMode==='range'
                ?<div style={S.rangeRow}>
                  <input style={S.filterInput} placeholder="Min $M" type="number" value={sf.mcap_min} onChange={e=>setSfF('mcap_min',e.target.value)} />
                  <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span>
                  <input style={S.filterInput} placeholder="Max $M" type="number" value={sf.mcap_max} onChange={e=>setSfF('mcap_max',e.target.value)} />
                </div>
                :<div>
                  <div style={{...S.colGrid,marginBottom:'8px'}}>
                    {mcapTiers.map(t=>(
                      <button key={t.key} style={S.colToggle(!mcapExcluded.has(t.key))} onClick={()=>{const n=new Set(mcapExcluded);n.has(t.key)?n.delete(t.key):n.add(t.key);setMcapExcluded(n);}}>
                        {t.label} <span style={{fontSize:'10px',opacity:0.7}}>{mcapTierLabel(t)}</span>
                      </button>
                    ))}
                  </div>
                  <div style={{display:'flex',gap:'6px'}}>
                    <span style={{fontSize:'11px',color:'var(--text-muted)',alignSelf:'center'}}>Tier scale:</span>
                    <button style={{...S.colToggle(mcapTierDefs==='us'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>{setMcapTierDefs('us');setMcapExcluded(new Set());}}>US</button>
                    <button style={{...S.colToggle(mcapTierDefs==='asx'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>{setMcapTierDefs('asx');setMcapExcluded(new Set());}}>ASX</button>
                  </div>
                </div>
              }
            </div>

            <div style={S.filterGrid}>
              <div>
                <FieldLabel helpText={METRIC_HELP.price} activeHelp={helpText} setActiveHelp={setHelpText}>Price</FieldLabel>
                <div style={S.rangeRow}>
                 <input style={S.filterInput} placeholder="Min" type="number" value={sf.price_min} onChange={e=>setSfF('price_min',e.target.value)} />
                  <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span>
                  <input style={S.filterInput} placeholder="Max" type="number" value={sf.price_max} onChange={e=>setSfF('price_max',e.target.value)} />
                </div>
              </div>
              <div>
                <FieldLabel helpText={METRIC_HELP.rsi} activeHelp={helpText} setActiveHelp={setHelpText}>RSI (14) — each optional</FieldLabel>
                <div style={S.rangeRow}>
                  <input style={S.filterInput} placeholder="Above" type="number" min={0} max={100} value={sf.rsi_min} onChange={e=>setSfF('rsi_min',e.target.value)} />
                  <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to below</span>
                  <input style={S.filterInput} placeholder="Below" type="number" min={0} max={100} value={sf.rsi_max} onChange={e=>setSfF('rsi_max',e.target.value)} />
                </div>
              </div>
              <div>
                <FieldLabel helpText={METRIC_HELP.stoch} activeHelp={helpText} setActiveHelp={setHelpText}>Stoch %K</FieldLabel>
                <div style={S.rangeRow}>
                  <input style={S.filterInput} placeholder="Min" type="number" min={0} max={100} value={sf.stoch_min} onChange={e=>setSfF('stoch_min',e.target.value)} />
                  <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span>
                  <input style={S.filterInput} placeholder="Max" type="number" min={0} max={100} value={sf.stoch_max} onChange={e=>setSfF('stoch_max',e.target.value)} />
                </div>
              </div>
              <div>
                <FieldLabel helpText={METRIC_HELP.volume} activeHelp={helpText} setActiveHelp={setHelpText}>Min Volume</FieldLabel>
                <input style={S.filterInput} placeholder="e.g. 100000" type="number" value={sf.volume_min} onChange={e=>setSfF('volume_min',e.target.value)} />
              </div>
              <div>
                <FieldLabel helpText={METRIC_HELP.div_yield} activeHelp={helpText} setActiveHelp={setHelpText}>Div Yield %</FieldLabel>
                <div style={S.rangeRow}>
                  <input style={S.filterInput} placeholder="Min" type="number" step={0.1} value={sf.yield_min} onChange={e=>setSfF('yield_min',e.target.value)} />
                  <span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span>
                  <input style={S.filterInput} placeholder="Max" type="number" step={0.1} value={sf.yield_max} onChange={e=>setSfF('yield_max',e.target.value)} />
                </div>
              </div>
              <div>
                <FieldLabel helpText={METRIC_HELP.bb_position} activeHelp={helpText} setActiveHelp={setHelpText}>BB Position</FieldLabel>
                <select style={S.filterSelect} value={sf.bb_position} onChange={e=>setSfF('bb_position',e.target.value)}>
                  <option value="">Any</option>
                  <option value="above_upper">Above Upper Band</option>
                  <option value="below_lower">Below Lower Band</option>
                  <option value="inside">Inside Bands</option>
                </select>
              </div>
              <div>
                <FieldLabel helpText={METRIC_HELP.atr} activeHelp={helpText} setActiveHelp={setHelpText}>ATR % of Price ≥</FieldLabel>
                <input style={S.filterInput} placeholder="e.g. 2" type="number" step={0.1} min={0} value={sf.atr_pct_min} onChange={e=>setSfF('atr_pct_min',e.target.value)} />
                <span style={{fontSize:'10px',color:'var(--text-muted)',display:'block',marginTop:'2px'}}>Filters for volatile stocks</span>
              </div>
              <div>
                <FieldLabel helpText={METRIC_HELP.obv} activeHelp={helpText} setActiveHelp={setHelpText}>OBV Direction</FieldLabel>
                <div style={{display:'flex',gap:'6px',alignItems:'center',flexWrap:'wrap'}}>
                  <select style={S.filterSelect} value={sf.obv_direction} onChange={e=>setSfF('obv_direction',e.target.value)}>
                    <option value="">Any</option>
                    <option value="rising">Rising</option>
                    <option value="falling">Falling</option>
                  </select>
                  {sf.obv_direction&&(
                    <label style={{fontSize:'11px',color:'var(--text-muted)',display:'flex',alignItems:'center',gap:'4px'}}>
                      over <input style={{...S.filterInput,width:'50px'}} type="number" min={2} max={50} value={sf.obv_days} onChange={e=>setSfF('obv_days',e.target.value)} /> days
                    </label>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px'}}>
                <FieldLabel helpText={METRIC_HELP.ema_rules} activeHelp={helpText} setActiveHelp={setHelpText} style={{marginBottom:0}}>EMA Crossover Rules</FieldLabel>
                <button style={S.addRuleBtn} onClick={addEma}>+ Add</button>
              </div>
              {emaRules.map(r=>(
                <div key={r.id} style={S.ruleRow}>
                  <select style={S.ruleSelect} value={r.ema1} onChange={e=>updEma(r.id,'ema1',e.target.value)}>{EMA_OPTIONS.map(o=><option key={o} value={o}>EMA {o}</option>)}</select>
                  <select style={S.ruleSelect} value={r.dir}  onChange={e=>updEma(r.id,'dir',e.target.value)}><option value="above">above</option><option value="below">below</option></select>
                  <select style={S.ruleSelect} value={r.ema2} onChange={e=>updEma(r.id,'ema2',e.target.value)}>{EMA_OPTIONS.map(o=><option key={o} value={o}>EMA {o}</option>)}</select>
                  <button style={S.removeBtn} onClick={()=>remEma(r.id)}>✕</button>
                </div>
              ))}
            </div>

            <div>
              <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px'}}>
                <FieldLabel helpText={METRIC_HELP.price_ema_rules} activeHelp={helpText} setActiveHelp={setHelpText} style={{marginBottom:0}}>Price vs EMA Rules</FieldLabel>
                <button style={S.addRuleBtn} onClick={addPr}>+ Add</button>
              </div>
              {priceEmaRules.map(r=>(
                <div key={r.id} style={S.ruleRow}>
                  <span style={{fontSize:'12px'}}>Price</span>
                  <select style={S.ruleSelect} value={r.dir} onChange={e=>updPr(r.id,'dir',e.target.value)}><option value="above">above</option><option value="below">below</option></select>
                  <select style={S.ruleSelect} value={r.ema} onChange={e=>updPr(r.id,'ema',e.target.value)}>{EMA_OPTIONS.map(o=><option key={o} value={o}>EMA {o}</option>)}</select>
                  <button style={S.removeBtn} onClick={()=>remPr(r.id)}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PRICE ACTION TAB ──────────────────────────────────────────── */}
        {activeTab==='priceaction'&&(
          <div>
            <p style={{fontSize:'12px',color:'var(--text-muted)',marginBottom:'14px',marginTop:0}}>
              Conditions evaluated against recent candle data. Toggle each condition on to configure it. Click ⓘ for details.
            </p>

            {[
              { key:'vol_spike',          label:'Volume spike — last candle volume % greater than N-day average' },
              { key:'big_green',          label:'Big green candle — positive close with body ≥ N% of price range' },
              { key:'range_expansion',    label:'Price range expansion — last candle high–low range ≥ N% greater than previous day' },
              { key:'gap',                label:'Gap — today\'s open gaps away from yesterday\'s close by ≥ N%' },
              { key:'inside_outside_bar', label:'Inside / Outside bar — last candle range relative to previous candle' },
              { key:'n_day_breakout',     label:'N-day breakout — close breaks above/below the N-day high or low' },
              { key:'week52_proximity',   label:'Near 52-week high — close is within N% of the 52-week high' },
              { key:'consecutive_closes', label:'Consecutive closes — N days in a row closing higher or lower' },
              { key:'ema_slope',          label:'EMA slope — an EMA is rising or falling over N days' },
            ].map(({ key, label }) => (
              <div key={key}>
                <div className="cond-toggle-row">
                  <Toggle checked={barConds[key].enabled} onChange={v=>setBC(key,{enabled:v})} />
                  <div style={{flex:1}}>
                    <span className="cond-toggle-label">{label}</span>
                    {BAR_HELP[key] && (
                      <InlineHelp text={BAR_HELP[key]} activeHelp={helpText} setActiveHelp={setHelpText} />
                    )}
                  </div>
                </div>
                {barConds[key].enabled && key==='vol_spike' && (
                  <div className="cond-params">
                    <ParamInput label="Volume ≥" value={barConds.vol_spike.pct} onChange={v=>setBC('vol_spike',{pct:parseFloat(v)||150})} min={100} max={1000} step={10} unit="% of avg" />
                    <ParamInput label="Avg over" value={barConds.vol_spike.days} onChange={v=>setBC('vol_spike',{days:parseInt(v)||20})} min={2} max={100} unit="days" />
                  </div>
                )}
                {barConds[key].enabled && key==='big_green' && (
                  <div className="cond-params">
                    <ParamInput label="Body ≥" value={barConds.big_green.body_pct} onChange={v=>setBC('big_green',{body_pct:parseFloat(v)||60})} min={20} max={100} step={5} unit="% of candle range" />
                    <ParamInput label="vs avg candle size over" value={barConds.big_green.avg_days} onChange={v=>setBC('big_green',{avg_days:parseInt(v)||10})} min={2} max={50} unit="days" />
                  </div>
                )}
                {barConds[key].enabled && key==='range_expansion' && (
                  <div className="cond-params">
                    <ParamInput label="Range ≥" value={barConds.range_expansion.pct} onChange={v=>setBC('range_expansion',{pct:parseFloat(v)||50})} min={10} max={500} step={10} unit="% greater than prev day" />
                  </div>
                )}
                {barConds[key].enabled && key==='gap' && (
                  <div className="cond-params">
                    <ParamSelect label="Direction" value={barConds.gap.direction} onChange={v=>setBC('gap',{direction:v})} options={[['up','Gap Up'],['down','Gap Down'],['either','Either']]} />
                    <ParamInput label="≥" value={barConds.gap.pct} onChange={v=>setBC('gap',{pct:parseFloat(v)||2})} min={0.1} max={50} step={0.5} unit="%" />
                  </div>
                )}
                {barConds[key].enabled && key==='inside_outside_bar' && (
                  <div className="cond-params">
                    <ParamSelect label="Type" value={barConds.inside_outside_bar.type} onChange={v=>setBC('inside_outside_bar',{type:v})} options={[['inside','Inside bar'],['outside','Outside bar']]} />
                  </div>
                )}
                {barConds[key].enabled && key==='n_day_breakout' && (
                  <div className="cond-params">
                    <ParamSelect label="Direction" value={barConds.n_day_breakout.direction} onChange={v=>setBC('n_day_breakout',{direction:v})} options={[['high','Break above N-day high'],['low','Break below N-day low']]} />
                    <ParamInput label="N =" value={barConds.n_day_breakout.days} onChange={v=>setBC('n_day_breakout',{days:parseInt(v)||52})} min={5} max={252} unit="days" />
                  </div>
                )}
                {barConds[key].enabled && key==='week52_proximity' && (
                  <div className="cond-params">
                    <ParamInput label="Within" value={barConds.week52_proximity.pct_from} onChange={v=>setBC('week52_proximity',{pct_from:parseFloat(v)||5})} min={0.5} max={30} step={0.5} unit="% of 52w high" />
                  </div>
                )}
                {barConds[key].enabled && key==='consecutive_closes' && (
                  <div className="cond-params">
                    <ParamSelect label="Direction" value={barConds.consecutive_closes.direction} onChange={v=>setBC('consecutive_closes',{direction:v})} options={[['up','Closing higher'],['down','Closing lower']]} />
                    <ParamInput label="N =" value={barConds.consecutive_closes.count} onChange={v=>setBC('consecutive_closes',{count:parseInt(v)||3})} min={2} max={20} unit="days" />
                  </div>
                )}
                {barConds[key].enabled && key==='ema_slope' && (
                  <div className="cond-params">
                    <ParamSelect label="EMA" value={barConds.ema_slope.ema} onChange={v=>setBC('ema_slope',{ema:v})} options={EMA_OPTIONS.map(o=>[o,`EMA ${o}`])} />
                    <ParamSelect label="is" value={barConds.ema_slope.direction} onChange={v=>setBC('ema_slope',{direction:v})} options={[['rising','Rising'],['falling','Falling']]} />
                    <ParamInput label="over" value={barConds.ema_slope.days} onChange={v=>setBC('ema_slope',{days:parseInt(v)||5})} min={2} max={50} unit="days" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── PATTERNS TAB ─────────────────────────────────────────────── */}
        {activeTab==='patterns'&&(
          <div>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'12px'}}>
              <p style={{fontSize:'12px',color:'var(--text-muted)',margin:0}}>
                Chart pattern detection runs against full bar history per instrument. Click ⓘ on any pattern for a full description and parameter guide.
              </p>
              <AddPatternDropdown onAdd={addPattern} />
            </div>
            {patternScans.length===0&&(
              <div style={{textAlign:'center',padding:'32px',color:'var(--text-muted)',fontSize:'12px',border:'1px dashed var(--border)',borderRadius:'var(--radius)'}}>
                No patterns added. Click <strong>+ Add Pattern</strong> to choose from 19 pattern types.
              </div>
            )}
            {patternScans.map((p,i)=>(
              <PatternBlock key={i} pattern={p} onChange={(u)=>updatePattern(i,u)} onRemove={()=>removePattern(i)} activeHelp={helpText} setActiveHelp={setHelpText} />
            ))}
            {patternScans.length>1&&(
              <div style={{display:'flex',gap:'8px',alignItems:'center',marginTop:'10px'}}>
                <label style={{...S.filterLabel,marginBottom:0}}>Combine patterns with</label>
                <select style={S.filterSelect} value={patternOp} onChange={e=>setPatternOp(e.target.value)}>
                  <option value="OR">OR — instrument matches any pattern</option>
                  <option value="AND">AND — instrument matches all patterns</option>
                </select>
              </div>
            )}
          </div>
        )}

        {error&&<div style={S.modalError}>{error}</div>}
        <div style={S.modalFooter}>
          <button style={S.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          {isEdit
            ? <>
                <button style={S.btnSecondary} onClick={handleSaveAsNew} disabled={busy}>{busy?'Saving…':'Save as New'}</button>
                <button style={S.btn} onClick={handleSave} disabled={busy}>{busy?'Saving…':'Save'}</button>
              </>
            : <button style={S.btn} onClick={handleSaveAsNew} disabled={busy}>{busy?'Creating…':'Create Scan'}</button>
          }
        </div>
      </div>
    </div>
  );
}

// ── ScanView ──────────────────────────────────────────�───────────────────────
function ScanView({ allData, tabDef, tabState, onStateChange, onTickerClick, ruleIdRef, ingestionStatus, onRunComplete }) {
  const {
    _id, basicCols, perfCols, perfType, searchRules,
    filters, mcapMode, mcapTierDefs, mcapExcluded,
    colExcluded, emaRules, priceEmaRules,
    sort, page, rowsPerPage, advOpen,
  } = tabState;

  const set  = (patch) => onStateChange(prev=>({...prev,...patch}));
  const setF = (k,v)   => set({filters:{...filters,[k]:v}});
  const mcapTiers = mcapTierDefs==='us'?MCAP_TIERS_US:MCAP_TIERS_ASX;

  const [colDropOpen,  setColDropOpen]  = useState(null);
  const [selected,     setSelected]     = useState(new Set());
  const [expanded,     setExpanded]     = useState(new Set());
  const [missingCount, setMissingCount] = useState(0);
  const [runMsg,       setRunMsg]       = useState('');
  const [running,      setRunning]      = useState(false);

  const staleness = useMemo(()=>getStaleness(tabDef, ingestionStatus),[tabDef, ingestionStatus]);

  const pollRef = useRef(null);
  useEffect(()=>{
    if(staleness?.type==='running'&&tabDef?.definition_id){
      pollRef.current = setInterval(async()=>{
        try{
          const r = await api.get(`/scans/status/${tabDef.definition_id}`);
          const s = r.data.status;
          if(s==='complete'||s==='error'){ clearInterval(pollRef.current); if(s==='complete') onRunComplete&&onRunComplete(); }
        }catch(e){ clearInterval(pollRef.current); }
      }, POLL_INTERVAL_MS);
    }
    return()=>{ if(pollRef.current) clearInterval(pollRef.current); };
  },[staleness, tabDef?.definition_id, onRunComplete]);

  const tabData = useMemo(()=>{
    if(!tabDef?.definition_id) return allData;
    return allData.filter(r=>r.scan_definition_id===tabDef.definition_id);
  },[allData, tabDef]);

  const grouped = useMemo(()=>groupByTicker(tabData),[tabData]);

  const filtered = useMemo(()=>{
    let miss=0;
    const f=filters;
    const result=grouped.filter(row=>{
      if(searchRules.some(r=>r.value)){
        let res=null;
        for(let i=0;i<searchRules.length;i++){
          const r=searchRules[i];
          if(!r.value)continue;
          const fields=r.field==='all'?[row.symbol,row.name,row.sector,row.industry,row.industry_group,row.sub_industry]:[row[r.field]||''];
          const val=r.caseSensitive?r.value:r.value.toLowerCase();
          let match=false;
          for(const fv of fields){if(!fv)continue;const fvs=r.caseSensitive?fv:fv.toLowerCase();if(r.op==='contains'&&fvs.includes(val))match=true;else if(r.op==='equals'&&fvs===val)match=true;else if(r.op==='starts'&&fvs.startsWith(val))match=true;else if(r.op==='ends'&&fvs.endsWith(val))match=true;if(match)break;}
          if(i===0)res=r.connector==='NOT'?!match:match;
          else if(r.connector==='AND')res=res&&match;
          else if(r.connector==='OR')res=res||match;
          else if(r.connector==='NOT')res=res&&!match;
        }
        if(res===false)return false;
      }
      const inR=(v,mn,mx)=>{if(mn===''&&mx==='')return true;if(v==null)return false;if(mn!==''&&!isNaN(mn)&&v<parseFloat(mn))return false;if(mx!==''&&!isNaN(mx)&&v>parseFloat(mx))return false;return true;};
      const inDate=(v,from,to)=>{if(!from&&!to)return true;if(!v)return false;if(from&&v<from)return false;if(to&&v>to)return false;return true;};
      if(!inR(row.expense_ratio!=null?row.expense_ratio*100:null,f.mer_min,f.mer_max))return false;
      if(mcapMode==='range'){if(!inR(row.market_cap!=null?row.market_cap/1e6:null,f.mcap_min,f.mcap_max))return false;}
      else if(mcapExcluded.size>0){const mc=row.market_cap;if(mc==null)return false;const tier=mcapTiers.find(t=>mc>=t.min&&mc<t.max);if(!tier||mcapExcluded.has(tier.key))return false;}
      if(!inR(row.last_price,f.price_min,f.price_max))return false;
      if(!inR(row.rsi14,f.rsi_min,f.rsi_max))return false;
      if(!inR(row.stoch_k,f.stoch_min,f.stoch_max))return false;
      if(f.volume_min!==''&&!isNaN(f.volume_min)&&(row.volume==null||row.volume<parseFloat(f.volume_min)))return false;
      if(!inR(row.dividend_yield!=null?row.dividend_yield*100:null,f.yield_min,f.yield_max))return false;
      if(f.bb_position){const p=row.last_price,bu=row.bb_upper,bl=row.bb_lower;if(p==null||bu==null||bl==null)return false;if(f.bb_position==='above_upper'&&p<=bu)return false;if(f.bb_position==='below_lower'&&p>=bl)return false;if(f.bb_position==='inside'&&(p<=bl||p>=bu))return false;}
      for(const rule of emaRules){if(!rule.ema1||!rule.ema2)continue;const v1=row[emaKey(rule.ema1)],v2=row[emaKey(rule.ema2)];if(v1==null||v2==null){miss++;return false;}if(rule.dir==='above'&&v1<=v2)return false;if(rule.dir==='below'&&v1>=v2)return false;}
      for(const rule of priceEmaRules){if(!rule.ema)continue;const p=row.last_price,ev=row[emaKey(rule.ema)];if(p==null||ev==null){miss++;return false;}if(rule.dir==='above'&&p<=ev)return false;if(rule.dir==='below'&&p>=ev)return false;}
      const scanRows=row._scanRows||[row];
      const passesAny=scanRows.some(sr=>{
        if(!inDate(sr.pattern_start_date,f.pat_start_from,f.pat_start_to))return false;
        if(!inDate(sr.pattern_end_date,f.pat_end_from,f.pat_end_to))return false;
        if(!inDate(sr.trigger_date,f.trigger_from,f.trigger_to))return false;
        if(!inR(sr.target_price,f.target_min,f.target_max))return false;
        return true;
      });
      if(!passesAny)return false;
      return true;
    });
    setMissingCount(miss);
    return result;
  },[grouped,searchRules,filters,emaRules,priceEmaRules,mcapMode,mcapExcluded,mcapTiers]);// eslint-disable-line

  const colFiltered=useMemo(()=>filtered.filter(row=>{
    for(const[ck,excSet]of Object.entries(colExcluded)){
      if(!excSet||excSet.size===0)continue;
      const val=ck==='exchange'?row.exchange||'':ck==='type'?row.asset_type||'':row[ck]||'';
      if(excSet.has(val))return false;
    }
    return true;
  }),[filtered,colExcluded]);

  const colValues=useMemo(()=>{
    const v={exchange:new Set(),type:new Set(),sector:new Set(),industry:new Set(),industry_group:new Set(),sub_industry:new Set()};
    filtered.forEach(r=>{v.exchange.add(r.exchange||'');v.type.add(r.asset_type||'');v.sector.add(r.sector||'');v.industry.add(r.industry||'');v.industry_group.add(r.industry_group||'');v.sub_industry.add(r.sub_industry||'');});
    return v;
  },[filtered]);

  function getSortVal(row,col,pt){
    if(col==='type')return row.asset_type;
    if(col==='exchange')return row.exchange;
    if(col.startsWith('perf_')){const period=col.replace('perf_','');const isAnn=pt.endsWith('_annualized');const base=isAnn?pt.replace('_annualized',''):pt;const raw=row[base+'_'+period];return isAnn?annualize(raw,period):raw;}
    return row[col];
  }

  const sorted=useMemo(()=>[...colFiltered].sort((a,b)=>{
    const av=getSortVal(a,sort.col,perfType),bv=getSortVal(b,sort.col,perfType);
    if(av==null&&bv==null)return 0;if(av==null)return 1;if(bv==null)return -1;
    const na=parseFloat(av),nb=parseFloat(bv);
    if(!isNaN(na)&&!isNaN(nb))return sort.dir==='ASC'?na-nb:nb-na;
    return sort.dir==='ASC'?String(av).localeCompare(String(bv)):String(bv).localeCompare(String(av));
  }),[colFiltered,sort,perfType]);// eslint-disable-line

  const totalPages=Math.max(1,Math.ceil(sorted.length/rowsPerPage));
  const pageData=sorted.slice((page-1)*rowsPerPage,page*rowsPerPage);
  const handleSort=(col)=>set({sort:{col,dir:sort.col===col&&sort.dir==='ASC'?'DESC':'ASC'},page:1});

  const toggleBasicCol=(k)=>set({basicCols:basicCols.includes(k)?basicCols.filter(c=>c!==k):[...basicCols,k]});
  const togglePerfCol=(p)=>{const n=perfCols.includes(p)?perfCols.filter(c=>c!==p):[...perfCols,p];set({perfCols:PERF_PERIODS.filter(x=>n.includes(x)),page:1});};
  const toggleColExclude=(ck,v,allVals)=>{const cur=colExcluded[ck]?new Set(colExcluded[ck]):new Set();if(v==='__DESELECT_ALL__'){allVals.forEach(x=>cur.add(x));}else{cur.has(v)?cur.delete(v):cur.add(v);}set({colExcluded:{...colExcluded,[ck]:cur},page:1});};
  const clearColExclude=(ck)=>{const n={...colExcluded};delete n[ck];set({colExcluded:n,page:1});};
  const addSR=()=>{const id=++ruleIdRef.current;set({searchRules:[...searchRules,{id,connector:'AND',field:'all',op:'contains',value:'',caseSensitive:false}]});};
  const remSR=(id)=>set({searchRules:searchRules.filter(r=>r.id!==id)});
  const updSR=(id,k,v)=>set({searchRules:searchRules.map(r=>r.id===id?{...r,[k]:v}:r)});
  const addEma=()=>{const id=++ruleIdRef.current;set({emaRules:[...emaRules,{id,ema1:'50',ema2:'200',dir:'above'}]});};
  const remEma=(id)=>set({emaRules:emaRules.filter(r=>r.id!==id)});
  const updEma=(id,k,v)=>set({emaRules:emaRules.map(r=>r.id===id?{...r,[k]:v}:r)});
  const addPr=()=>{const id=++ruleIdRef.current;set({priceEmaRules:[...priceEmaRules,{id,ema:'50',dir:'above'}]});};
  const remPr=(id)=>set({priceEmaRules:priceEmaRules.filter(r=>r.id!==id)});
  const updPr=(id,k,v)=>set({priceEmaRules:priceEmaRules.map(r=>r.id===id?{...r,[k]:v}:r)});
  const clearFilters=()=>set({filters:defaultTabState(_id).filters,searchRules:defaultTabState(_id).searchRules,emaRules:[],priceEmaRules:[],mcapExcluded:new Set(),colExcluded:{},page:1});

  const tickerKey=(row)=>`${row.symbol}:${row.exchange}`;
  const toggleRow=(row)=>{const k=tickerKey(row);setSelected(p=>{const n=new Set(p);n.has(k)?n.delete(k):n.add(k);return n;});};
  const toggleExpand=(row)=>{const k=tickerKey(row);setExpanded(p=>{const n=new Set(p);n.has(k)?n.delete(k):n.add(k);return n;});};
  const togglePage=()=>{
    const keys=pageData.map(tickerKey);
    const all=keys.every(k=>selected.has(k));
    setSelected(p=>{const n=new Set(p);if(all)keys.forEach(k=>n.delete(k));else keys.forEach(k=>n.add(k));return n;});
  };
  const pageAllChecked=pageData.length>0&&pageData.every(r=>selected.has(tickerKey(r)));
  const pagePartial=!pageAllChecked&&pageData.some(r=>selected.has(tickerKey(r)));

  const headers=useMemo(()=>{
    const h=[{key:'exchange',label:'Region'},{key:'symbol',label:'Symbol'},{key:'name',label:'Name'}];
    BASIC_COL_DEFS.filter(c=>basicCols.includes(c.key)).forEach(c=>h.push({key:c.key,label:c.label}));
    perfCols.forEach(p=>h.push({key:'perf_'+p,label:p.toUpperCase()}));
    return h;
  },[basicCols,perfCols]);

  function renderCell(row,key){
    if(key==='exchange'){const s=row.exchange==='AU'?{background:'#dbeafe',color:'#1d4ed8'}:{background:'#f1f8e9',color:'#388e3c'};return <span style={{...S.badge,...s}}>{row.exchange}</span>;}
    if(key==='symbol')return <span style={{color:'var(--accent)',fontWeight:700,cursor:'pointer'}} onClick={()=>onTickerClick&&onTickerClick(`${row.symbol}:${row.exchange}`)}>{row.symbol}</span>;
    if(key==='name')return <span style={{maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',display:'inline-block'}}>{row.name||'-'}</span>;
    if(key==='type'){const tm={equity:{background:'#f0fdf4',color:'#15803d'},etf:{background:'#f3e8ff',color:'#7c3aed'}};return <span style={{...S.badge,...(tm[row.asset_type]||{})}}>{row.asset_type||'-'}</span>;}
    if(key==='sector')return <span style={{fontSize:'11px'}}>{row.sector||'-'}</span>;
    if(key==='industry')return <span style={{fontSize:'11px'}}>{row.industry||'-'}</span>;
    if(key==='industry_group')return <span style={{fontSize:'11px'}}>{row.industry_group||'-'}</span>;
    if(key==='sub_industry')return <span style={{fontSize:'11px'}}>{row.sub_industry||'-'}</span>;
    if(key==='idx_tracked')return <span style={{fontSize:'11px'}}>{row.idx_tracked||'-'}</span>;
    if(key==='provider_name')return <span style={{fontSize:'11px'}}>{row.provider_name||'-'}</span>;
    if(key==='expense_ratio')return fmtMer(row.expense_ratio);
    if(key==='market_cap')return fmtMktCap(row.market_cap);
    if(key==='last_price')return fmtPrice(row.last_price);
    if(key==='pe_ratio')return fmt(row.pe_ratio,1);
    if(key==='dividend_yield')return fmtPct(row.dividend_yield);
    if(key==='volume')return fmtVol(row.volume);
    if(key==='description')return <span style={{fontSize:'11px',whiteSpace:'normal',wordWrap:'break-word',display:'inline-block',maxWidth:'500px'}}>{row.description||'-'}</span>;
    if(key.startsWith('perf_')){
      const period=key.replace('perf_','');
      const isAnn=perfType.endsWith('_annualized');
      const base=isAnn?perfType.replace('_annualized',''):perfType;
      const raw=row[base+'_'+period];
      const val=isAnn?annualize(raw,period):raw;
      if(val==null)return '-';
      return <span style={val>=0?S.positive:S.negative}>{val.toFixed(2)}%</span>;
    }
    return '-';
  }

  function renderHeader(h){
    const isF=FILTERABLE_COLS.has(h.key);
    const si=sort.col===h.key?(sort.dir==='ASC'?' ▲':' ▼'):' ⇅';
    if(!isF)return <>{h.label}{si}</>;
    const ck=h.key;
    const excSet=colExcluded[ck]||new Set();
    const vals=colValues[ck]||new Set();
    const count=excSet.size;
    const isOpen=colDropOpen===ck;
    return (
      <div style={{position:'relative',display:'inline-flex',alignItems:'center',gap:'4px'}}>
        <span onClick={()=>handleSort(h.key)}>{h.label}{si}</span>
        <span onClick={e=>{e.stopPropagation();setColDropOpen(isOpen?null:ck);}}
          style={{cursor:'pointer',fontSize:'10px',padding:'1px 5px',borderRadius:'3px',background:count>0?'#fef3c7':'rgba(255,255,255,0.15)',color:count>0?'#92400e':'#fff',fontWeight:count>0?700:400}}
          title="Filter values">▾{count>0?` (${count})`:''}</span>
        {isOpen&&<ColFilterDropdown values={vals} excluded={excSet} onToggle={(v)=>toggleColExclude(ck,v,[...vals])} onClear={()=>clearColExclude(ck)} onClose={()=>setColDropOpen(null)} />}
      </div>
    );
  }

  function pageButtons(){
    const btns=[],delta=2;
    const left=Math.max(2,page-delta),right=Math.min(totalPages-1,page+delta);
    btns.push(1);if(left>2)btns.push('...');
    for(let i=left;i<=right;i++)btns.push(i);
    if(right<totalPages-1)btns.push('...');
    if(totalPages>1)btns.push(totalPages);
    return btns;
  }

  const handleRunScan=async()=>{
    if(!tabDef?.definition_id)return;
    setRunning(true);setRunMsg('');
    try{
      const r=await api.post(`/scans/run/${tabDef.definition_id}`);
      setRunMsg(r.data.message||'Queued');
      onRunComplete&&onRunComplete();
    }catch(e){setRunMsg(e.response?.data?.error||'Error');}
    finally{setRunning(false);}
  };

  const hasBeenRun = !!(tabDef?.last_run_candle_date_au || tabDef?.last_run_candle_date_us);

  const renderStatusBar=()=>{
    if(!staleness)return null;
    if(staleness.type==='error')return <div style={S.runBarRed}><span>✗ {staleness.message}</span></div>;
    if(staleness.type==='fetching')return <div style={S.runBarBlue}><span>⟳ {staleness.message}</span></div>;
    if(staleness.type==='running')return(
      <div style={S.runBarBlue}>
        <span>⟳ {staleness.message}</span>
        {runMsg&&<span style={{fontSize:'11px'}}>{runMsg}</span>}
      </div>
    );
    if(staleness.type==='stale')return(
      <div style={S.runBarYellow}>
        <span>⚠ {staleness.message}</span>
        <button style={S.runBtn} onClick={handleRunScan} disabled={running}>{running?'Queuing…':'▶ Run Scan'}</button>
        {runMsg&&<span style={{fontSize:'11px'}}>{runMsg}</span>}
      </div>
    );
    return null;
  };

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0}}>
      {renderStatusBar()}
      <div style={{flexShrink:0}}>
        <div style={S.advHeader} onClick={()=>set({advOpen:!advOpen})}>
          <span>⚙ FILTERS & COLUMNS</span><span>{advOpen?'▲':'▼'}</span>
        </div>
        {advOpen&&(
          <div style={S.advContent}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px'}}>
              <div>
                <strong style={{fontSize:'12px',display:'block',marginBottom:'8px',color:'var(--text)'}}>Basic Columns</strong>
                <div style={S.colGrid}>{BASIC_COL_DEFS.map(c=><button key={c.key} style={S.colToggle(basicCols.includes(c.key))} onClick={()=>toggleBasicCol(c.key)}>{c.label}</button>)}</div>
              </div>
              <div>
                <strong style={{fontSize:'12px',display:'block',marginBottom:'8px',color:'var(--text)'}}>Performance Columns</strong>
                <div style={S.colGrid}>{PERF_PERIODS.map(p=><button key={p} style={S.colToggle(perfCols.includes(p))} onClick={()=>togglePerfCol(p)}>{p.toUpperCase()}</button>)}</div>
                <div style={{display:'flex',gap:'16px',marginTop:'10px',flexWrap:'wrap'}}>
                  {[['growth','Growth (Cumul.)'],['growth_annualized','Growth (Ann.)'],['drip','Total Return (Cumul.)'],['drip_annualized','Total Return (Ann.)']].map(([v,l])=>(
                    <label key={v} style={{fontSize:'12px',display:'flex',alignItems:'center',gap:'4px',cursor:'pointer'}}>
                      <input type="radio" name={`perfType-${_id}`} value={v} checked={perfType===v} onChange={()=>set({perfType:v})} />{l}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <strong style={{fontSize:'12px',display:'block',marginBottom:'10px',color:'var(--text)'}}>Data Filters</strong>
              <div style={S.filterGrid}>
                <div><span style={S.filterLabel}>MER % (ETFs)</span><div style={S.rangeRow}><input style={S.filterInput} placeholder="Min" type="number" value={filters.mer_min} onChange={e=>setF('mer_min',e.target.value)} /><span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span><input style={S.filterInput} placeholder="Max" type="number" value={filters.mer_max} onChange={e=>setF('mer_max',e.target.value)} /></div></div>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:'6px',marginBottom:'4px'}}>
                    <span style={S.filterLabel}>Market Cap</span>
                    <button style={{...S.colToggle(mcapMode==='range'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>set({mcapMode:'range',mcapExcluded:new Set()})}>Range</button>
                    <button style={{...S.colToggle(mcapMode==='tiers'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>set({mcapMode:'tiers',filters:{...filters,mcap_min:'',mcap_max:''}})}>Tiers</button>
                  </div>
                  {mcapMode==='range'
                    ?<div style={S.rangeRow}><input style={S.filterInput} placeholder="Min $M" type="number" value={filters.mcap_min} onChange={e=>setF('mcap_min',e.target.value)} /><span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span><input style={S.filterInput} placeholder="Max $M" type="number" value={filters.mcap_max} onChange={e=>setF('mcap_max',e.target.value)} /></div>
                    :<div style={S.colGrid}>{(mcapTierDefs==='us'?MCAP_TIERS_US:MCAP_TIERS_ASX).map(t=><button key={t.key} style={S.colToggle(!mcapExcluded.has(t.key))} onClick={()=>{const n=new Set(mcapExcluded);n.has(t.key)?n.delete(t.key):n.add(t.key);set({mcapExcluded:n});}}>{t.label} <span style={{fontSize:'10px',opacity:0.7}}>{mcapTierLabel(t)}</span></button>)}</div>
                  }
                  <div style={{display:'flex',gap:'8px',marginTop:'6px'}}>
                    <button style={{...S.colToggle(mcapTierDefs==='us'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>set({mcapTierDefs:'us',mcapExcluded:new Set()})}>US</button>
                    <button style={{...S.colToggle(mcapTierDefs==='asx'),padding:'2px 8px',fontSize:'10px'}} onClick={()=>set({mcapTierDefs:'asx',mcapExcluded:new Set()})}>ASX</button>
                  </div>
                </div>
                <div><span style={S.filterLabel}>Price</span><div style={S.rangeRow}><input style={S.filterInput} placeholder="Min" type="number" value={filters.price_min} onChange={e=>setF('price_min',e.target.value)} /><span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span><input style={S.filterInput} placeholder="Max" type="number" value={filters.price_max} onChange={e=>setF('price_max',e.target.value)} /></div></div>
                <div><span style={S.filterLabel}>RSI (14)</span><div style={S.rangeRow}><input style={S.filterInput} placeholder="Min" type="number" value={filters.rsi_min} onChange={e=>setF('rsi_min',e.target.value)} /><span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span><input style={S.filterInput} placeholder="Max" type="number" value={filters.rsi_max} onChange={e=>setF('rsi_max',e.target.value)} /></div></div>
                <div><span style={S.filterLabel}>Stoch %K</span><div style={S.rangeRow}><input style={S.filterInput} placeholder="Min" type="number" value={filters.stoch_min} onChange={e=>setF('stoch_min',e.target.value)} /><span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span><input style={S.filterInput} placeholder="Max" type="number" value={filters.stoch_max} onChange={e=>setF('stoch_max',e.target.value)} /></div></div>
                <div><span style={S.filterLabel}>Volume ≥</span><input style={S.filterInput} placeholder="e.g. 100000" type="number" value={filters.volume_min} onChange={e=>setF('volume_min',e.target.value)} /></div>
                <div><span style={S.filterLabel}>Div Yield %</span><div style={S.rangeRow}><input style={S.filterInput} placeholder="Min" type="number" value={filters.yield_min} onChange={e=>setF('yield_min',e.target.value)} /><span style={{fontSize:'11px',color:'var(--text-muted)'}}>to</span><input style={S.filterInput} placeholder="Max" type="number" value={filters.yield_max} onChange={e=>setF('yield_max',e.target.value)} /></div></div>
              </div>
            </div>
            <div>
              <div style={{display:'flex',gap:'8px',alignItems:'center',marginBottom:'8px'}}><strong style={{fontSize:'12px',color:'var(--text)'}}>Text Search</strong><button style={S.addRuleBtn} onClick={addSR}>+ Add Rule</button></div>
              {searchRules.map(r=>(
                <div key={r.id} style={S.ruleRow}>
                  <select style={S.ruleSelect} value={r.connector} onChange={e=>updSR(r.id,'connector',e.target.value)}><option value="AND">AND</option><option value="OR">OR</option><option value="NOT">NOT</option></select>
                  <select style={S.ruleSelect} value={r.field} onChange={e=>updSR(r.id,'field',e.target.value)}><option value="all">All Fields</option><option value="symbol">Symbol</option><option value="name">Name</option><option value="sector">Sector</option><option value="industry">Industry</option><option value="industry_group">Industry Group</option><option value="sub_industry">Sub-Industry</option></select>
                  <select style={S.ruleSelect} value={r.op} onChange={e=>updSR(r.id,'op',e.target.value)}><option value="contains">Contains</option><option value="equals">Equals</option><option value="starts">Starts With</option><option value="ends">Ends With</option></select>
                  <input style={{...S.filterInput,width:'160px'}} placeholder="Search term..." value={r.value} onChange={e=>updSR(r.id,'value',e.target.value)} />
                  <label style={{fontSize:'11px',display:'flex',alignItems:'center',gap:'3px',cursor:'pointer'}}><input type="checkbox" checked={r.caseSensitive} onChange={e=>updSR(r.id,'caseSensitive',e.target.checked)} /> Aa</label>
                  <button style={S.removeBtn} onClick={()=>remSR(r.id)}>✕</button>
                </div>
              ))}
            </div>
            <div style={{display:'flex',justifyContent:'flex-end'}}>
              <button style={S.btnSecondary} onClick={clearFilters}>Clear All Filters</button>
            </div>
          </div>
        )}
      </div>

      <div style={S.tableArea}>
        {missingCount>0&&<div style={S.missingNote}>⚠ {missingCount} result{missingCount!==1?'s':''} excluded due to missing technical data</div>}
        <div style={S.statsBar}>
          <span><strong style={{color:'var(--accent)',fontSize:'16px'}}>{sorted.length.toLocaleString()}</strong> tickers</span>
          <div style={{display:'flex',alignItems:'center',gap:'8px',fontSize:'12px'}}>
            <span>Rows:</span>
            <input type="number" min="10" max="500" value={rowsPerPage} onChange={e=>set({rowsPerPage:Math.max(10,parseInt(e.target.value)||50),page:1})} style={{width:'60px',padding:'3px 6px',border:'1px solid var(--border)',borderRadius:'4px',fontSize:'12px',background:'var(--bg)',color:'var(--text)'}} />
            <span>Page {page} of {totalPages}</span>
          </div>
        </div>
        {selected.size>0&&(
          <div style={S.selBar}>
            <span style={S.selCount}>{selected.size} selected</span>
            {onTickerClick&&<button style={S.btnSm} onClick={()=>Array.from(selected).forEach(t=>onTickerClick(t))}>Open Selected</button>}
            <button style={{...S.btnGhost,fontSize:'12px'}} onClick={()=>setSelected(new Set())}>Clear</button>
          </div>
        )}
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.thExpand} />
                <th style={S.thCheck} onClick={togglePage}>
                  <input type="checkbox" readOnly checked={pageAllChecked} ref={el=>{if(el)el.indeterminate=pagePartial;}} style={{cursor:'pointer'}} />
                </th>
                {headers.map(h=>(
                  <th key={h.key} style={S.th} onClick={()=>{if(!FILTERABLE_COLS.has(h.key))handleSort(h.key);}}>
                    {renderHeader(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length===0
                ?<tr><td colSpan={headers.length+2} style={{...S.td,textAlign:'center',color:'var(--text-muted)',padding:'48px 20px'}}>
                    {hasBeenRun
                      ? <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'8px'}}>
                          <span style={{fontSize:'22px'}}>🔍</span>
                          <span style={{fontWeight:600,fontSize:'13px',color:'var(--text)'}}>No results found</span>
                          <span style={{fontSize:'12px',maxWidth:'420px',lineHeight:'1.6'}}>
                            Your scan conditions may be too strict or mutually exclusive. Try relaxing one filter at a time — for example, avoid combining "BB above upper band" with resistance patterns, as stocks breaking out rarely have a flat ceiling to bounce from.
                          </span>
                        </div>
                      : <span>Run the scan to populate results.</span>
                    }
                  </td></tr>
                :pageData.flatMap((row,i)=>{
                  const k=tickerKey(row);
                  const checked=selected.has(k);
                  const isExp=expanded.has(k);
                  const scanRows=row._scanRows||[row];
                  const bg=checked?'var(--accent-dim)':i%2===0?'var(--surface)':'var(--bg)';
                  return [
                    <tr key={k} style={{background:bg}}
                      onMouseEnter={e=>{if(!checked)e.currentTarget.style.background='var(--accent-dim)';}}
                      onMouseLeave={e=>{if(!checked)e.currentTarget.style.background=bg;}}>
                      <td style={S.tdExpand} onClick={()=>toggleExpand(row)}>
                        {isExp?'▼':'▶'}
                        {scanRows.length>1&&<span style={{fontSize:'10px',marginLeft:'2px',opacity:0.7}}>{scanRows.length}</span>}
                      </td>
                      <td style={S.tdCheck} onClick={()=>toggleRow(row)}><input type="checkbox" readOnly checked={checked} style={{cursor:'pointer'}} /></td>
                      {headers.map(h=>(
                        <td key={h.key} style={{...S.td,whiteSpace:h.key==='description'?'normal':'nowrap'}}>{renderCell(row,h.key)}</td>
                      ))}
                    </tr>,
                    isExp&&<ScanSubTable key={`${k}-sub`} scanRows={scanRows} />,
                  ].filter(Boolean);
                })
              }
            </tbody>
          </table>
        </div>
        {totalPages>1&&(
          <div style={S.pagination}>
            <button style={S.pageBtn(false)} onClick={()=>set({page:1})} disabled={page===1}>«</button>
            <button style={S.pageBtn(false)} onClick={()=>set({page:Math.max(1,page-1)})} disabled={page===1}>‹</button>
            <div style={{display:'flex',gap:'4px'}}>
              {pageButtons().map((b,idx)=>b==='...'
                ?<span key={`e${idx}`} style={{padding:'0 4px',color:'var(--text-muted)'}}>…</span>
                :<button key={b} style={S.pageBtn(page===b)} onClick={()=>set({page:b})}>{b}</button>
              )}
            </div>
            <button style={S.pageBtn(false)} onClick={()=>set({page:Math.min(totalPages,page+1)})} disabled={page===totalPages}>›</button>
            <button style={S.pageBtn(false)} onClick={()=>set({page:totalPages})} disabled={page===totalPages}>»</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ScanSelector ──────────────────────────────────────────────────────────────
function ScanSelector({ userTabs, openTabIds, onToggleTab, onOpenModal, onOpenDetails, onDeleteTab, onEditTab }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(()=>{
    const h=(e)=>{ if(ref.current&&!ref.current.contains(e.target))setOpen(false); };
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);
  return (
    <div style={S.selectorBar}>
      <div style={{position:'relative'}} ref={ref}>
        <button style={S.selectorBtn} onClick={()=>setOpen(p=>!p)}>
          ☰ My Scans {userTabs.length>0&&`(${userTabs.length})`} {open?'▲':'▼'}
        </button>
        {open&&(
          <div style={S.dropdown}>
            {userTabs.length===0&&<div style={{...S.dropItem,color:'var(--text-muted)',fontStyle:'italic'}}>No scans yet</div>}
            {userTabs.map(t=>(
              <div key={t.id} style={{...S.dropItem,background:openTabIds.has(t.id)?'var(--accent-dim)':'transparent',justifyContent:'space-between'}}>
                <span style={{cursor:'pointer',flex:1,color:openTabIds.has(t.id)?'var(--accent)':'var(--text)'}} onClick={()=>{onToggleTab(t);setOpen(false);}}>
                  {openTabIds.has(t.id)?'✓ ':''}{t.tab_name}
                </span>
                <div style={{display:'flex',gap:'4px',flexShrink:0}}>
                  <button style={{...S.tabBtn,fontSize:'11px'}} title="Edit scan" onClick={e=>{e.stopPropagation();onEditTab(t);setOpen(false);}}>✎</button>
                  <button style={{...S.tabBtn,fontSize:'11px'}} title="Details" onClick={e=>{e.stopPropagation();onOpenDetails(t);setOpen(false);}}>ⓘ</button>
                  <button style={{...S.tabBtn,fontSize:'11px',color:'#dc2626'}} title="Delete" onClick={e=>{e.stopPropagation();onDeleteTab(t);setOpen(false);}}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <button style={{...S.selectorBtn,borderColor:'var(--accent)',color:'var(--accent)'}} onClick={onOpenModal}>
        + New Scan
      </button>
    </div>
  );
}

// ── ChartContent ──────────────────────────────────────────────────────────────
function ChartContent({ tabs, activeTab, onSelect, onRefresh, onClose, onTickerChange }) {
  const ChartTabStripComp = useMemo(()=>makeTabStrip(onClose),[onClose]);
  return (
    <div style={S.chartArea}>
      <ChartTabStripComp tabs={tabs} activeTab={activeTab} onSelect={onSelect} onRefresh={onRefresh} />
      {tabs.length===0?(
        <div style={S.noTabs}>
          <span style={{fontSize:'28px'}}>📈</span>
          <span>Click a ticker to open a chart</span>
        </div>
      ):tabs.map(t=>(
        <div key={t.id} style={{flex:1,overflow:'hidden',flexDirection:'column',display:'flex',
          visibility:t.id===activeTab?'visible':'hidden',
          position:t.id===activeTab?'relative':'absolute'}}>
          <ChartPage embedded initialTicker={t.initialTicker} refreshKey={t.refreshKey} onTickerChange={(nt)=>onTickerChange(t.id,nt)} />
        </div>
      ))}
    </div>
  );
}

// ── ScansPanel ────────────────────────────────────────────────────────────────
function ScansPanel({ onTickerClick }) {
  const ruleIdRef     = useRef(1);

  const [allData,         setAllData]         = useState([]);
  const [userTabs,        setUserTabs]        = useState([]);
  const [ingestionStatus, setIngestionStatus] = useState({});
  const [loading,         setLoading]         = useState(true);
  const [openTabIds,      setOpenTabIds]      = useState(new Set());
  const [activeTab,       setActiveTab]       = useState(null);
  const [tabStates,       setTabStates]       = useState({});
  const [showNewModal,    setShowNewModal]    = useState(false);
  const [editTab,         setEditTab]         = useState(null);
  const [detailsTab,      setDetailsTab]      = useState(null);
  const [deleteTab,       setDeleteTab]       = useState(null);
  const [deleteBusy,      setDeleteBusy]      = useState(false);

  const handleCloseTab=useCallback((stripId)=>{
    const tabId=parseInt(stripId.replace('tab-',''),10);
    setOpenTabIds(p=>{const n=new Set(p);n.delete(tabId);return n;});
    setActiveTab(cur=>cur===stripId?null:cur);
  },[]);
  const ScanTabStripComp=useMemo(()=>makeTabStrip(handleCloseTab),[handleCloseTab]);

  const stripTabs=useMemo(()=>
    userTabs.filter(ut=>openTabIds.has(ut.id)).map(ut=>({
      id:`tab-${ut.id}`,
      label:ut.tab_name,
      definition_id:ut.definition_id,
      rules_json:ut.rules_json,
      last_run_candle_date_au:ut.last_run_candle_date_au,
      last_run_candle_date_us:ut.last_run_candle_date_us,
      last_run_at:ut.last_run_at,
      last_run_status:ut.last_run_status,
      last_run_error:ut.last_run_error,
      last_run_count:ut.last_run_count,
      description:ut.description,
    }))
  ,[userTabs,openTabIds]);

  const fetchAll=useCallback(async()=>{
    const [d,t]=await Promise.all([api.get('/scans/data'),api.get('/scans/tabs')]);
    setAllData(Array.isArray(d.data)?d.data:[]);
    setUserTabs(t.data.tabs||[]);
    setIngestionStatus(t.data.ingestion_status||{});
  },[]);

  useEffect(()=>{
    setLoading(true);
    fetchAll().catch(()=>{}).finally(()=>setLoading(false));
  },[fetchAll]);

  const handleToggleTab=useCallback((userTab)=>{
    const tabId=userTab.id;
    const stripId=`tab-${tabId}`;
    setOpenTabIds(p=>{
      const n=new Set(p);
      if(n.has(tabId)){
        n.delete(tabId);
        setActiveTab(cur=>cur===stripId?null:cur);
      }else{
        n.add(tabId);
        setTabStates(prev=>prev[stripId]?prev:{...prev,[stripId]:defaultTabState(stripId)});
        setActiveTab(stripId);
      }
      return n;
    });
  },[]);

  const handleScanCreated=useCallback(async(data, mode)=>{
    await fetchAll();
    if(mode==='created') {
      const t=await api.get('/scans/tabs');
      const tabs=t.data.tabs||[];
      setUserTabs(tabs);
      const newest=tabs[tabs.length-1];
      if(newest){
        const stripId=`tab-${newest.id}`;
        setOpenTabIds(p=>{const n=new Set(p);n.add(newest.id);return n;});
        setTabStates(p=>p[stripId]?p:{...p,[stripId]:defaultTabState(stripId)});
        setActiveTab(stripId);
      }
    }
  },[fetchAll]);

  const handleDeleteConfirm=useCallback(async()=>{
    if(!deleteTab)return;
    setDeleteBusy(true);
    try{
      await api.delete(`/scans/tabs/${deleteTab.id}`);
      const stripId=`tab-${deleteTab.id}`;
      setOpenTabIds(p=>{const n=new Set(p);n.delete(deleteTab.id);return n;});
      setActiveTab(cur=>cur===stripId?null:cur);
      await fetchAll();
      setDeleteTab(null);
    }catch(e){console.error(e);}
    finally{setDeleteBusy(false);}
  },[deleteTab,fetchAll]);

  const updateTabState=useCallback((stripId,updater)=>{
    setTabStates(p=>({...p,[stripId]:updater(p[stripId]||defaultTabState(stripId))}));
  },[]);

  const getTabDef=(stripId)=>stripTabs.find(t=>t.id===stripId)||null;

  if(loading)return(
    <div style={{...S.scanPage,alignItems:'center',justifyContent:'center',gap:'12px'}}>
      <style>{KEYFRAMES}</style>
      <div style={{width:'32px',height:'32px',border:'3px solid var(--border)',borderTopColor:'var(--accent)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}} />
      <span style={{color:'var(--text-muted)',fontSize:'13px'}}>Loading scans…</span>
    </div>
  );

  return (
    <div style={S.scanPage}>
      <style>{KEYFRAMES}</style>
      {stripTabs.length>0&&(
        <ScanTabStripComp tabs={stripTabs} activeTab={activeTab} onSelect={setActiveTab} />
      )}
      <ScanSelector
        userTabs={userTabs} openTabIds={openTabIds}
        onToggleTab={handleToggleTab}
        onOpenModal={()=>setShowNewModal(true)}
        onOpenDetails={(t)=>setDetailsTab(t)}
        onDeleteTab={(t)=>setDeleteTab(t)}
        onEditTab={(t)=>setEditTab(t)}
      />
      {stripTabs.length===0?(
        <div style={S.emptyState}>
          <span style={{fontSize:'32px'}}>🔍</span>
          <span style={{fontWeight:600,fontSize:'14px',color:'var(--text)'}}>No scan tabs open</span>
          <span style={{fontSize:'12px'}}>
            {userTabs.length>0
              ?'Open a scan from the dropdown, or create a new one.'
              :'Click + New Scan to create your first scan.'}
          </span>
        </div>
      ):stripTabs.map(t=>(
        <div key={t.id} style={{flex:1,display:'flex',flexDirection:'column',minHeight:0,overflow:'hidden',
          visibility:activeTab===t.id?'visible':'hidden',
          position:activeTab===t.id?'relative':'absolute',width:'100%',height:'100%'}}>
          <ScanView
            allData={allData}
            tabDef={getTabDef(t.id)}
            tabState={tabStates[t.id]||defaultTabState(t.id)}
            onStateChange={(u)=>updateTabState(t.id,u)}
            onTickerClick={onTickerClick}
            ruleIdRef={ruleIdRef}
            ingestionStatus={ingestionStatus}
            onRunComplete={fetchAll}
          />
        </div>
      ))}
      {showNewModal&&<NewScanModal onClose={()=>setShowNewModal(false)} onCreated={handleScanCreated} />}
      {editTab&&<NewScanModal editDef={editTab} onClose={()=>setEditTab(null)} onCreated={handleScanCreated} />}
      {detailsTab&&<ScanDetailsModal tab={detailsTab} onClose={()=>setDetailsTab(null)} />}
      {deleteTab&&<DeleteConfirmModal tab={deleteTab} onClose={()=>setDeleteTab(null)} onConfirm={handleDeleteConfirm} busy={deleteBusy} />}
    </div>
  );
}

// ── ScansWorkspace ────────────────────────────────────────────────────────────
export default function ScansWorkspace() {
  const { user, loading } = useAuth();
  const navigate                  = useNavigate();

  const [chartTabs,   setChartTabs]   = useState([]);
  const [activeChart, setActiveChart] = useState(null);
  const [panelWidth,  setPanelWidth]  = useState(()=>Math.round(window.innerWidth*0.65));
  const [isMobile,    setIsMobile]    = useState(window.innerWidth<MOBILE_BP);
  const tabIdRef   = useRef(0);
  const dragging   = useRef(false);
  const dragStartX = useRef(0);
  const dragStartW = useRef(0);
  const tabTickers = useRef({});
  const widthRef   = useRef(Math.round(window.innerWidth*0.65));

  useEffect(()=>{ document.title='Scans | woody.wtf'; },[]);
  useEffect(()=>{ if(!loading&&!user)navigate('/login',{state:{from:'/scans'}}); },[user,loading,navigate]);
  useEffect(()=>{ if(isMobile)navigate('/scanner',{replace:true}); },[isMobile,navigate]);
  useEffect(()=>{
    const h=()=>setIsMobile(window.innerWidth<MOBILE_BP);
    window.addEventListener('resize',h);
    return()=>window.removeEventListener('resize',h);
  },[]);

  const handleTickerClick=useCallback((ticker)=>{
    setChartTabs(prev=>{
      const existing=prev.find(t=>tabTickers.current[t.id]===ticker||t.initialTicker===ticker);
      if(existing){setActiveChart(existing.id);return prev;}
      const id=++tabIdRef.current;
      tabTickers.current[id]=ticker;
      setActiveChart(id);
      return[...prev,{id,label:ticker,initialTicker:ticker,refreshKey:0}];
    });
  },[]);

  const handleChartSelect  =useCallback((id)=>setActiveChart(id),[]);
  const handleChartRefresh =useCallback((id)=>setChartTabs(prev=>prev.map(t=>t.id===id?{...t,refreshKey:t.refreshKey+1}:t)),[]);
  const handleChartClose   =useCallback((id)=>{
    delete tabTickers.current[id];
    setChartTabs(prev=>{
      const next=prev.filter(t=>t.id!==id);
      setActiveChart(cur=>{
        if(cur!==id)return cur;
        const idx=prev.findIndex(t=>t.id===id);
        return next[Math.max(0,idx-1)]?.id||next[0]?.id||null;
      });
      return next;
    });
  },[]);
  const handleTickerChange=useCallback((tabId,newTicker)=>{
    setChartTabs(prev=>{
      const existing=prev.find(t=>t.id!==tabId&&(tabTickers.current[t.id]===newTicker||t.initialTicker===newTicker));
      if(existing){setActiveChart(existing.id);return prev;}
      tabTickers.current[tabId]=newTicker;
      return prev.map(t=>t.id===tabId?{...t,label:newTicker}:t);
    });
  },[]);

  const onResizeMouseDown=useCallback((e)=>{
    e.preventDefault();
    dragging.current=true;
    dragStartX.current=e.clientX;
    dragStartW.current=panelWidth;
  },[panelWidth]);

  useEffect(()=>{
    const onMove=(e)=>{
      if(!dragging.current)return;
      const delta=dragStartX.current-e.clientX;
      const maxW=window.innerWidth*PANEL_MAX;
      widthRef.current=Math.min(maxW,Math.max(PANEL_MIN,dragStartW.current+delta));
      const p=document.getElementById('sc-chart-panel');
      if(p)p.style.width=widthRef.current+'px';
      const l=document.getElementById('sc-left-pane');
      if(l)l.style.flex=`0 0 calc(100% - ${widthRef.current}px - 5px)`;
    };
    const onUp=()=>{ if(!dragging.current)return; dragging.current=false; setPanelWidth(widthRef.current); };
    document.addEventListener('mousemove',onMove);
    document.addEventListener('mouseup',onUp);
    return()=>{ document.removeEventListener('mousemove',onMove); document.removeEventListener('mouseup',onUp); };
  },[]);


  if(loading||!user)return<div style={S.loadingScreen}>Loading…</div>;
  if(isMobile)return null;

  const chartOpen=chartTabs.length>0;

  return (
    <div style={S.root}>
      <Navbar rightLinks={[
        { label: 'Workspace', href: '/workspace', muted: true },
        { label: 'Scans', href: '/scans', muted: true },
      ]} />
      <div style={S.body}>
        <div id="sc-left-pane" style={{
          flex:chartOpen?`0 0 calc(100% - ${panelWidth}px - 5px)`:'1 1 auto',
          overflow:'hidden',display:'flex',flexDirection:'column',minWidth:0,
        }}>
          <ScansPanel onTickerClick={handleTickerClick} />
        </div>
        {chartOpen&&(
          <>
            <div style={S.resizeHandle} onMouseDown={onResizeMouseDown} title="Drag to resize" />
            <div id="sc-chart-panel" style={S.chartPanel(panelWidth)}>
              <ChartContent
                tabs={chartTabs} activeTab={activeChart}
                onSelect={handleChartSelect} onRefresh={handleChartRefresh}
                onClose={handleChartClose} onTickerChange={handleTickerChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
